/**
 * Elite Batch 20 Part 3 — Workflow Templates 1888–1907
 * Review site listing outreach, risky catch-all quarantine, SMS nudge after
 * consent, sales manager daily call list, sales manager recruiting pipeline,
 * Sales Navigator enrichment pipeline, Salesforce contact upsert, Salesforce
 * enrichment batch, school district research pipeline, security review
 * stakeholder map, security review submission, sender domain auth alert,
 * sender router by lead tier, Sendy self-hosted batch send, shared investor
 * path, stale opportunity reactivation, stale signal cleanup, strategic
 * partnership outreach, subject-line variant generator, support frustration
 * save play.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function hubspotGetNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}
function hubspotPostNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  bodyFields: Array<{ key: string; value: string }>,
  outputs: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyFields,
        variables: outputs,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 20 PART 3 — TEMPLATES 1888–1907
// =============================================================================

export const ELITE_BATCH20_PART3: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 1888. REVIEW SITE LISTING OUTREACH (8 nodes)
  // ===================================================================

  "Review Site Listing Outreach": {
    summary:
      "Starts manually for a listing campaign, AI researches review site requirements and analyzes listing fit, fetches site editor contact via API, AI drafts the listing application email, branches by site authority, sends the application email, and tracks listing status in Google Sheets.",
    nodes: [
      trigger("Manual: Review site listing campaign", "manual", {
        howItStarts:
          "Triggered manually when a new review site listing campaign is launched, providing the target review site URL, product name, and listing category for outreach",
        setup:
          "Configure OpenAI API key for research and drafting. Set Hunter.io API key for editor contact discovery. Configure email sending credentials. Set Google Sheets credentials for tracking.",
        inputFields: [
          {
            name: "review_site_url",
            type: "string",
            example: "https://g2.com/products/acme",
          },
          { name: "product_name", type: "string", example: "OmniEngage" },
          {
            name: "listing_category",
            type: "string",
            example: "Marketing Automation",
          },
          {
            name: "product_description",
            type: "string",
            example: "AI-powered workflow automation platform",
          },
        ],
      }),

      // 2. AI research review site requirements
      aiNode(
        "2",
        "AI Research Review Site Requirements",
        280,
        "You are a review site research expert. Given the review site URL and category, research and identify the listing requirements, editorial guidelines, typical listing turnaround, authority metrics (DA, traffic tier), and any submission fees. Respond JSON: {site_name, domain_authority, traffic_tier, listing_requirements, editorial_guidelines, turnaround_days, submission_fee, content_requirements, review_minimum, acceptance_rate_estimate}",
        "Review site: {review_site_url} | Category: {listing_category} | Product: {product_name}. Research listing requirements and site authority metrics.",
        [
          { field: "site_name", as: "site_name", type: "string" },
          { field: "domain_authority", as: "domain_authority", type: "number" },
          { field: "traffic_tier", as: "traffic_tier", type: "string" },
          {
            field: "listing_requirements",
            as: "listing_requirements",
            type: "string",
          },
          {
            field: "editorial_guidelines",
            as: "editorial_guidelines",
            type: "string",
          },
          { field: "turnaround_days", as: "turnaround_days", type: "string" },
          { field: "submission_fee", as: "submission_fee", type: "string" },
          {
            field: "content_requirements",
            as: "content_requirements",
            type: "string",
          },
          {
            field: "acceptance_rate_estimate",
            as: "acceptance_rate",
            type: "string",
          },
        ],
      ),

      // 3. AI analyze listing fit
      aiNode(
        "3",
        "AI Analyze Product-Site Listing Fit",
        520,
        "You are a listing strategy analyst. Evaluate whether the product is a strong fit for the review site based on requirements, category alignment, and competitive landscape. Provide a fit score 0-100, gaps to address, and recommended positioning angle. Respond JSON: {fit_score, fit_assessment, gaps_to_address, positioning_angle, competitive_advantage, recommended_screenshots, key_features_to_highlight}",
        "Product: {product_name} — {product_description} | Site: {site_name} | Requirements: {listing_requirements} | Content Needs: {content_requirements} | Category: {listing_category} | DA: {domain_authority}. Assess listing fit and strategy.",
        [
          { field: "fit_score", as: "listing_fit_score", type: "number" },
          { field: "fit_assessment", as: "fit_assessment", type: "string" },
          { field: "gaps_to_address", as: "gaps_to_address", type: "string" },
          {
            field: "positioning_angle",
            as: "positioning_angle",
            type: "string",
          },
          {
            field: "competitive_advantage",
            as: "competitive_advantage",
            type: "string",
          },
          {
            field: "key_features_to_highlight",
            as: "key_features",
            type: "string",
          },
        ],
      ),

      // 4. Fetch site editor contact via Hunter API
      n("4", "hitApi", "Fetch Site Editor Contact via Hunter", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hunter.io/v2/domain-search?domain={review_site_url}&api_key={hunter_api_key}&department=editorial",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "data.emails[0].value",
              customName: "editor_email",
              type: "string",
            },
            {
              name: "data.emails[0].first_name",
              customName: "editor_first_name",
              type: "string",
            },
            {
              name: "data.emails[0].last_name",
              customName: "editor_last_name",
              type: "string",
            },
            {
              name: "data.emails[0].position",
              customName: "editor_position",
              type: "string",
            },
            {
              name: "data.emails[0].confidence",
              customName: "email_confidence",
              type: "number",
            },
          ],
        },
      }),

      // 5. AI draft listing application email
      aiNode(
        "5",
        "AI Draft Listing Application Email",
        1000,
        "You are an expert outreach copywriter specializing in review site listings. Draft a professional application email to the site editor requesting a product listing. Reference specific site requirements, highlight product strengths aligned to their editorial focus, and include a compelling reason to accept the listing. Keep it concise (under 200 words). Respond JSON: {subject_line, email_body, follow_up_plan}",
        "Editor: {editor_first_name} {editor_last_name} ({editor_position}) at {site_name} | Product: {product_name} — {product_description} | Fit Score: {listing_fit_score}/100 | Positioning: {positioning_angle} | Key Features: {key_features} | Requirements: {listing_requirements} | Guidelines: {editorial_guidelines}",
        [
          { field: "subject_line", as: "application_subject", type: "string" },
          { field: "email_body", as: "application_body", type: "string" },
          { field: "follow_up_plan", as: "follow_up_plan", type: "string" },
        ],
      ),

      // 6. Branch by site authority
      n("6", "condition", "Route by Site Authority Level", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "domain_authority", operator: "gte", value: "50" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 7. Send application email
      emailNode(
        "7",
        "Send Listing Application Email",
        1480,
        "{editor_email}",
        "{application_subject}",
        "{application_body}",
      ),

      // 8. Track listing status Sheets
      sheetsNode(
        "8",
        "Track Listing Status in Sheets",
        1720,
        "ListingOutreach",
        '[["{site_name}","{domain_authority}","{traffic_tier}","{editor_email}","{listing_fit_score}","{positioning_angle}","{application_subject}","{follow_up_plan}","Sent","' +
          new Date().toISOString().split("T")[0] +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("6", "7", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1889. RISKY CATCH-ALL QUARANTINE (7 nodes)
  // ===================================================================

  "Risky Catch-All Quarantine": {
    summary:
      "Fires when a catch-all domain is detected via webhook, validates the email via ZeroBounce API, checks domain reputation with runCode, scores deliverability risk with runCode, branches by risk level, routes to secondary verification API, and updates lead status in HubSpot CRM.",
    nodes: [
      trigger("Webhook: Catch-all domain detected", "webhook_app", {
        source: "zerobounce",
        event: "validation.catch_all",
        howItStarts:
          "Fires when an email validation service detects a catch-all domain, indicating the domain accepts all emails regardless of whether the specific mailbox exists — requiring additional verification before sending",
        setup:
          "Configure ZeroBounce webhook for catch-all detection events. Set ZeroBounce API key for deep validation. Set HubSpot API token for lead status updates.",
        inputFields: [],
      }),

      // 2. AI extract catch-all data
      aiNode(
        "2",
        "AI Extract Catch-All Detection Data",
        280,
        "You are an email validation data parser. Extract the email address, domain, validation status, catch-all confidence, MX records, SMTP response, sub-status, lead ID, and any additional validation metadata from the raw webhook payload. Respond JSON: {email_address, domain, validation_status, catch_all_confidence, mx_records, smtp_response, sub_status, lead_id, did_you_mean, free_email}",
        "Raw webhook payload: {webhook_payload}. Extract all catch-all validation data regardless of field naming conventions.",
        [
          { field: "email_address", as: "suspect_email", type: "string" },
          { field: "domain", as: "suspect_domain", type: "string" },
          {
            field: "validation_status",
            as: "validation_status",
            type: "string",
          },
          {
            field: "catch_all_confidence",
            as: "catch_all_confidence",
            type: "string",
          },
          { field: "mx_records", as: "mx_records", type: "string" },
          { field: "smtp_response", as: "smtp_response", type: "string" },
          { field: "sub_status", as: "sub_status", type: "string" },
          { field: "lead_id", as: "lead_id", type: "string" },
          { field: "free_email", as: "is_free_email", type: "string" },
        ],
      ),

      // 3. Check domain reputation runCode
      n("3", "runCode", "Check Domain Reputation Signals", 520, {
        runCodeDetails: {
          code: `const domain = variables.suspect_domain || '';
const mxRecords = variables.mx_records || '';
const smtpResponse = variables.smtp_response || '';
const isFree = variables.is_free_email === 'true';
const knownCatchAllProviders = ['godaddy.com','hostgator.com','bluehost.com','namecheap.com','ionos.com'];
const enterpriseMX = ['google.com','googlemail.com','outlook.com','microsoft.com','proofpoint.com','mimecast.com'];
const mxLower = mxRecords.toLowerCase();
const isKnownCatchAll = knownCatchAllProviders.some(p => mxLower.includes(p));
const isEnterpriseMX = enterpriseMX.some(p => mxLower.includes(p));
const hasValidSMTP = smtpResponse.includes('250') || smtpResponse.includes('OK');
let reputationScore = 50;
if (isEnterpriseMX) reputationScore += 30;
if (hasValidSMTP) reputationScore += 10;
if (isKnownCatchAll) reputationScore -= 25;
if (isFree) reputationScore -= 15;
if (domain.length <= 4) reputationScore -= 10;
output.domain_reputation_score = Math.max(0, Math.min(100, reputationScore));
output.is_known_catch_all_provider = isKnownCatchAll;
output.is_enterprise_mx = isEnterpriseMX;
output.has_valid_smtp = hasValidSMTP;
output.reputation_factors = 'MX:' + (isEnterpriseMX ? 'enterprise' : 'standard') + ' SMTP:' + (hasValidSMTP ? 'valid' : 'invalid') + ' CatchAll:' + (isKnownCatchAll ? 'yes' : 'no');`,
          outputVariables: [
            { name: "domain_reputation_score", type: "number" },
            { name: "is_known_catch_all_provider", type: "boolean" },
            { name: "is_enterprise_mx", type: "boolean" },
            { name: "has_valid_smtp", type: "boolean" },
            { name: "reputation_factors", type: "string" },
          ],
        },
      }),

      // 4. Score deliverability risk runCode
      n("4", "runCode", "Score Deliverability Risk Level", 760, {
        runCodeDetails: {
          code: `const reputationScore = parseInt(variables.domain_reputation_score || '50');
const catchAllConf = parseFloat(variables.catch_all_confidence || '0');
const isKnownCatchAll = variables.is_known_catch_all_provider === true || variables.is_known_catch_all_provider === 'true';
const validationStatus = variables.validation_status || '';
let riskScore = 0;
if (catchAllConf > 0.9) riskScore += 40;
else if (catchAllConf > 0.7) riskScore += 25;
else riskScore += 10;
if (isKnownCatchAll) riskScore += 20;
if (reputationScore < 40) riskScore += 25;
else if (reputationScore < 60) riskScore += 10;
if (validationStatus === 'catch-all') riskScore += 15;
const riskLevel = riskScore >= 60 ? 'high' : riskScore >= 35 ? 'medium' : 'low';
const action = riskLevel === 'high' ? 'quarantine' : riskLevel === 'medium' ? 'verify_secondary' : 'allow_with_monitoring';
output.deliverability_risk_score = Math.min(100, riskScore);
output.risk_level = riskLevel;
output.recommended_action = action;
output.risk_summary = 'Email: ' + variables.suspect_email + ' | Risk: ' + riskScore + '/100 (' + riskLevel + ') | Reputation: ' + reputationScore + ' | CatchAll Conf: ' + catchAllConf + ' | Action: ' + action;`,
          outputVariables: [
            { name: "deliverability_risk_score", type: "number" },
            { name: "risk_level", type: "string" },
            { name: "recommended_action", type: "string" },
            { name: "risk_summary", type: "string" },
          ],
        },
      }),

      // 5. Branch by risk level
      n("5", "condition", "Route by Deliverability Risk Level", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "risk_level", operator: "equals", value: "high" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 6. Route to secondary verification API
      n("6", "hitApi", "Route to Secondary Verification API", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.neverbounce.com/v4/single/check",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            { key: "key", value: "{neverbounce_api_key}" },
            { key: "email", value: "{suspect_email}" },
          ],
          variables: [
            { name: "result", customName: "secondary_result", type: "string" },
            { name: "flags", customName: "secondary_flags", type: "string" },
            {
              name: "suggested_correction",
              customName: "suggested_correction",
              type: "string",
            },
          ],
        },
      }),

      // 7. Update lead status CRM HubSpot
      hubspotPostNode(
        "7",
        "Update Lead Status in HubSpot CRM",
        1480,
        "crm/v3/objects/contacts/{lead_id}",
        [
          {
            key: "properties",
            value:
              '{"hs_lead_status":"{recommended_action}","email_validation_status":"{validation_status}","email_risk_score":"{deliverability_risk_score}","email_risk_level":"{risk_level}","secondary_verification":"{secondary_result}","notes_last_updated":"Catch-all quarantine: {risk_summary}. Secondary: {secondary_result} {secondary_flags}"}',
          },
        ],
        [
          { name: "id", customName: "updated_contact_id", type: "string" },
          {
            name: "properties.hs_lead_status",
            customName: "confirmed_status",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("5", "7", "output_2"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1890. SMS NUDGE AFTER CONSENT (8 nodes)
  // ===================================================================

  "SMS Nudge After Consent": {
    summary:
      "Fires when SMS consent is received via webhook, validates the phone number via Twilio API, checks consent compliance with runCode, fetches contact context from HubSpot, AI generates personalized SMS content, branches by phone line type, sends the SMS via Twilio, and logs SMS activity to HubSpot CRM.",
    nodes: [
      trigger("Webhook: SMS consent received", "webhook_app", {
        source: "hubspot",
        event: "contact.propertyChange",
        howItStarts:
          "Fires when a contact opts in to SMS communications via form submission, updating their SMS consent property and triggering the first personalized nudge message",
        setup:
          "Configure HubSpot webhook for SMS consent property changes. Set Twilio API credentials for phone validation and SMS sending. Set OpenAI API key for personalized messaging. Configure HubSpot API token for contact enrichment and activity logging.",
        inputFields: [],
      }),

      // 2. AI extract consent data
      aiNode(
        "2",
        "AI Extract SMS Consent Data",
        280,
        "You are a consent event parser. Extract the contact ID, phone number, consent source, consent timestamp, contact email, contact name, consent type (marketing/transactional), opt-in method, and any campaign context from the raw webhook payload. Respond JSON: {contact_id, phone_number, consent_source, consent_timestamp, contact_email, contact_name, consent_type, opt_in_method, campaign_context}",
        "Raw webhook payload: {webhook_payload}. Extract all SMS consent data regardless of field naming conventions.",
        [
          { field: "contact_id", as: "contact_id", type: "string" },
          { field: "phone_number", as: "raw_phone", type: "string" },
          { field: "consent_source", as: "consent_source", type: "string" },
          {
            field: "consent_timestamp",
            as: "consent_timestamp",
            type: "string",
          },
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "consent_type", as: "consent_type", type: "string" },
          { field: "opt_in_method", as: "opt_in_method", type: "string" },
          { field: "campaign_context", as: "campaign_context", type: "string" },
        ],
      ),

      // 3. Validate phone number via Twilio Lookup API
      n("3", "hitApi", "Validate Phone Number via Twilio Lookup", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://lookups.twilio.com/v2/PhoneNumbers/{raw_phone}?Fields=line_type_intelligence",
          headers: [
            { key: "Authorization", value: "Basic {twilio_auth_base64}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "valid", customName: "phone_valid", type: "boolean" },
            { name: "phone_number", customName: "e164_phone", type: "string" },
            {
              name: "country_code",
              customName: "phone_country",
              type: "string",
            },
            {
              name: "line_type_intelligence.type",
              customName: "line_type",
              type: "string",
            },
            {
              name: "line_type_intelligence.carrier_name",
              customName: "carrier_name",
              type: "string",
            },
          ],
        },
      }),

      // 4. Check consent compliance runCode
      n("4", "runCode", "Check SMS Consent Compliance", 760, {
        runCodeDetails: {
          code: `const isValid = variables.phone_valid === true || variables.phone_valid === 'true';
const lineType = (variables.line_type || '').toLowerCase();
const consentType = (variables.consent_type || '').toLowerCase();
const consentTimestamp = variables.consent_timestamp || '';
const optInMethod = (variables.opt_in_method || '').toLowerCase();
const consentAge = consentTimestamp ? Math.floor((Date.now() - new Date(consentTimestamp).getTime()) / (1000 * 60)) : 9999;
let complianceStatus = 'approved';
let complianceNotes = [];
if (!isValid) { complianceStatus = 'blocked'; complianceNotes.push('Invalid phone number'); }
if (lineType === 'landline') { complianceStatus = 'blocked'; complianceNotes.push('Landline cannot receive SMS'); }
if (consentAge > 1440 * 30) { complianceStatus = 'expired'; complianceNotes.push('Consent older than 30 days'); }
if (!optInMethod || optInMethod === 'unknown') { complianceNotes.push('Opt-in method not verified'); }
const isTCPA = consentType === 'marketing';
if (isTCPA) complianceNotes.push('TCPA marketing consent — include opt-out');
output.compliance_status = complianceStatus;
output.compliance_notes = complianceNotes.join('; ') || 'All checks passed';
output.consent_age_minutes = consentAge;
output.requires_opt_out_text = isTCPA;
output.is_mobile = lineType === 'mobile' || lineType === 'voip';`,
          outputVariables: [
            { name: "compliance_status", type: "string" },
            { name: "compliance_notes", type: "string" },
            { name: "consent_age_minutes", type: "number" },
            { name: "requires_opt_out_text", type: "boolean" },
            { name: "is_mobile", type: "boolean" },
          ],
        },
      }),

      // 5. Fetch contact context HubSpot
      hubspotGetNode(
        "5",
        "Fetch Contact Context from HubSpot",
        1000,
        "crm/v3/objects/contacts/{contact_id}?properties=firstname,lastname,company,lifecyclestage,hs_lead_status,recent_deal_amount,num_associated_deals,hs_analytics_source",
        [
          {
            name: "properties.firstname",
            customName: "first_name",
            type: "string",
          },
          {
            name: "properties.lastname",
            customName: "last_name",
            type: "string",
          },
          {
            name: "properties.company",
            customName: "company_name",
            type: "string",
          },
          {
            name: "properties.lifecyclestage",
            customName: "lifecycle_stage",
            type: "string",
          },
          {
            name: "properties.hs_lead_status",
            customName: "lead_status",
            type: "string",
          },
          {
            name: "properties.recent_deal_amount",
            customName: "deal_amount",
            type: "string",
          },
          {
            name: "properties.hs_analytics_source",
            customName: "traffic_source",
            type: "string",
          },
        ],
      ),

      // 6. AI personalized SMS content
      aiNode(
        "6",
        "AI Generate Personalized SMS Nudge",
        1240,
        "You are an expert SMS copywriter. Craft a short, personalized SMS nudge message (under 160 characters) based on the contact context and campaign. The tone should be friendly and value-driven. Include opt-out language if required. Respond JSON: {sms_message, message_type, personalization_hook, estimated_engagement}",
        "Contact: {first_name} {last_name} at {company_name} | Stage: {lifecycle_stage} | Lead Status: {lead_status} | Campaign: {campaign_context} | Consent Source: {consent_source} | Requires Opt-Out: {requires_opt_out_text}. Craft a compelling first SMS nudge.",
        [
          { field: "sms_message", as: "sms_content", type: "string" },
          { field: "message_type", as: "message_type", type: "string" },
          {
            field: "personalization_hook",
            as: "personalization_hook",
            type: "string",
          },
          {
            field: "estimated_engagement",
            as: "estimated_engagement",
            type: "string",
          },
        ],
      ),

      // 7. Branch by line type
      n("7", "condition", "Route by Phone Line Type", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "is_mobile", operator: "equals", value: "true" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 8. Send SMS via Twilio
      smsNode(
        "8",
        "Send SMS Nudge via Twilio",
        1720,
        "{e164_phone}",
        "{sms_content}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("7", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 1891. SALES MANAGER DAILY CALL LIST (9 nodes)
  // ===================================================================

  "Sales Manager Daily Call List": {
    summary:
      "Runs on a morning schedule, fetches at-risk deals and stalled pipeline from HubSpot, AI analyzes risk and coaching opportunities, AI drafts rep-specific call recommendations, ranks by business impact with runCode, branches by team size, delivers the call list to Slack, and tracks call completion in Google Sheets.",
    nodes: [
      trigger("Schedule: Morning sales call list", "schedule", {
        schedule: "Every weekday at 7:30 AM",
        howItStarts:
          "Runs every weekday morning to compile a prioritized daily call list for sales managers, identifying at-risk deals, stalled pipeline, and coaching opportunities across their team",
        setup:
          "Configure HubSpot API token for deal and pipeline queries. Set OpenAI API key for risk analysis and call recommendations. Configure Slack webhook for call list delivery. Set Google Sheets credentials for call tracking.",
        inputFields: [],
      }),

      // 2. Fetch at-risk deals HubSpot
      hubspotPostNode(
        "2",
        "Fetch At-Risk Deals from HubSpot",
        280,
        "crm/v3/objects/deals/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"dealstage","operator":"IN","values":["qualifiedtobuy","presentationscheduled","decisionmakerboughtin"]},{"propertyName":"hs_deal_stage_probability","operator":"LT","value":"50"},{"propertyName":"closedate","operator":"LT","value":"' +
              new Date(Date.now() + 30 * 86400000).toISOString().split("T")[0] +
              '"}]}]',
          },
          {
            key: "properties",
            value:
              '["dealname","amount","dealstage","closedate","hubspot_owner_id","hs_deal_stage_probability","notes_last_updated","hs_lastmodifieddate","pipeline"]',
          },
          { key: "limit", value: "50" },
        ],
        [
          { name: "results", customName: "at_risk_deals", type: "string" },
          { name: "total", customName: "at_risk_count", type: "number" },
        ],
      ),

      // 3. Fetch stalled pipeline HubSpot
      hubspotPostNode(
        "3",
        "Fetch Stalled Pipeline Deals from HubSpot",
        520,
        "crm/v3/objects/deals/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"hs_lastmodifieddate","operator":"LT","value":"' +
              new Date(Date.now() - 14 * 86400000).toISOString().split("T")[0] +
              '"},{"propertyName":"dealstage","operator":"NOT_IN","values":["closedwon","closedlost"]}]}]',
          },
          {
            key: "properties",
            value:
              '["dealname","amount","dealstage","closedate","hubspot_owner_id","hs_lastmodifieddate","pipeline","notes_last_updated"]',
          },
          { key: "limit", value: "50" },
        ],
        [
          { name: "results", customName: "stalled_deals", type: "string" },
          { name: "total", customName: "stalled_count", type: "number" },
        ],
      ),

      // 4. AI analyze risk and coaching opportunities
      aiNode(
        "4",
        "AI Analyze Risk and Coaching Opportunities",
        760,
        "You are a sales operations analyst. Review the at-risk and stalled deals to identify patterns: which reps have the most at-risk deals, common stall points in the pipeline, deals that need executive sponsorship, and coaching opportunities for specific reps. Respond JSON: {risk_patterns, top_risk_reps, stall_stage_analysis, executive_sponsor_needed, coaching_opportunities, total_at_risk_value, total_stalled_value}",
        "At-risk deals ({at_risk_count}): {at_risk_deals} | Stalled deals ({stalled_count}): {stalled_deals}. Analyze risk patterns, rep performance, and coaching opportunities.",
        [
          { field: "risk_patterns", as: "risk_patterns", type: "string" },
          { field: "top_risk_reps", as: "top_risk_reps", type: "string" },
          {
            field: "stall_stage_analysis",
            as: "stall_analysis",
            type: "string",
          },
          {
            field: "executive_sponsor_needed",
            as: "exec_sponsor_deals",
            type: "string",
          },
          {
            field: "coaching_opportunities",
            as: "coaching_opps",
            type: "string",
          },
          { field: "total_at_risk_value", as: "at_risk_value", type: "string" },
          { field: "total_stalled_value", as: "stalled_value", type: "string" },
        ],
      ),

      // 5. AI rep-specific call recommendations
      aiNode(
        "5",
        "AI Draft Rep-Specific Call Recommendations",
        1000,
        "You are a sales coaching expert. For each at-risk rep, draft specific call recommendations: which deal to call about first, what to say, what objection to prepare for, and what coaching feedback to deliver. Format as a prioritized call list per rep. Respond JSON: {call_list, priority_calls, coaching_notes, estimated_calls_needed, time_allocation}",
        "Risk Patterns: {risk_patterns} | Top Risk Reps: {top_risk_reps} | Stall Analysis: {stall_analysis} | Exec Sponsor Needed: {exec_sponsor_deals} | Coaching: {coaching_opps} | At-Risk Value: {at_risk_value} | Stalled Value: {stalled_value}. Create actionable call list per rep.",
        [
          { field: "call_list", as: "daily_call_list", type: "string" },
          { field: "priority_calls", as: "priority_calls", type: "string" },
          { field: "coaching_notes", as: "coaching_notes", type: "string" },
          {
            field: "estimated_calls_needed",
            as: "estimated_calls",
            type: "string",
          },
          { field: "time_allocation", as: "time_allocation", type: "string" },
        ],
      ),

      // 6. Rank by business impact runCode
      n("6", "runCode", "Rank Calls by Business Impact", 1240, {
        runCodeDetails: {
          code: `const atRiskCount = parseInt(variables.at_risk_count || '0');
const stalledCount = parseInt(variables.stalled_count || '0');
const atRiskValue = variables.at_risk_value || '0';
const stalledValue = variables.stalled_value || '0';
const totalDeals = atRiskCount + stalledCount;
const urgencyLevel = totalDeals > 20 ? 'critical' : totalDeals > 10 ? 'high' : totalDeals > 5 ? 'moderate' : 'low';
const teamSize = totalDeals > 0 ? Math.ceil(totalDeals / 5) : 1;
const estimatedCallTime = totalDeals * 15;
const reportDate = new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
output.total_action_items = totalDeals;
output.urgency_level = urgencyLevel;
output.estimated_team_size = teamSize;
output.estimated_call_minutes = estimatedCallTime;
output.report_date = reportDate;
output.impact_summary = reportDate + ': ' + atRiskCount + ' at-risk ($' + atRiskValue + ') + ' + stalledCount + ' stalled ($' + stalledValue + '). Urgency: ' + urgencyLevel + '. Est. ' + estimatedCallTime + ' min call time.';`,
          outputVariables: [
            { name: "total_action_items", type: "number" },
            { name: "urgency_level", type: "string" },
            { name: "estimated_team_size", type: "number" },
            { name: "estimated_call_minutes", type: "number" },
            { name: "report_date", type: "string" },
            { name: "impact_summary", type: "string" },
          ],
        },
      }),

      // 7. Branch by team size
      n("7", "condition", "Route by Team Size Threshold", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "estimated_team_size", operator: "gte", value: "3" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 8. Deliver call list to Slack
      slackNode(
        "8",
        "Deliver Daily Call List to Slack",
        1720,
        "Sales Manager Daily Call List — {report_date}\\n\\nUrgency: {urgency_level} | Action Items: {total_action_items} | Est. Call Time: {estimated_call_minutes} min\\n\\nAt-Risk Value: ${at_risk_value} ({at_risk_count} deals) | Stalled Value: ${stalled_value} ({stalled_count} deals)\\n\\nPriority Calls:\\n{priority_calls}\\n\\nFull Call List:\\n{daily_call_list}\\n\\nCoaching Notes:\\n{coaching_notes}\\n\\nTime Allocation: {time_allocation}",
      ),

      // 9. Track call completion Sheets
      sheetsNode(
        "9",
        "Track Call Completion in Sheets",
        1960,
        "DailyCallList",
        '[["{report_date}","{urgency_level}","{total_action_items}","{at_risk_count}","{at_risk_value}","{stalled_count}","{stalled_value}","{estimated_call_minutes}","{priority_calls}","Pending"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("7", "8", "output_2"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1892. SALES MANAGER RECRUITING PIPELINE (8 nodes)
  // ===================================================================

  "Sales Manager Recruiting Pipeline": {
    summary:
      "Fires when a sourcing request arrives via webhook, searches for candidates via API, enriches candidate profiles with Clearbit, AI assesses leadership experience, scores and ranks candidates with runCode, AI drafts personalized recruiting pitches, branches by candidate score, and sends recruiting outreach email.",
    nodes: [
      trigger("Webhook: Sales manager sourcing request", "webhook_app", {
        source: "greenhouse",
        event: "job_opening.created",
        howItStarts:
          "Fires when a new sales manager position sourcing request is created in the ATS, triggering automated candidate discovery, enrichment, and personalized outreach",
        setup:
          "Configure Greenhouse webhook for job opening events. Set LinkedIn/People API key for candidate search. Set Clearbit API key for enrichment. Set OpenAI API key for assessment and pitch drafting. Configure email sending credentials.",
        inputFields: [],
      }),

      // 2. AI extract sourcing request
      aiNode(
        "2",
        "AI Extract Sourcing Request Details",
        280,
        "You are a recruiting operations parser. Extract the job title, department, hiring manager, location, salary range, required experience years, required skills, company name, job ID, and urgency level from the raw webhook payload. Respond JSON: {job_title, department, hiring_manager, location, salary_range, experience_years, required_skills, company_name, job_id, urgency}",
        "Raw webhook payload: {webhook_payload}. Extract all sourcing request details regardless of field naming conventions.",
        [
          { field: "job_title", as: "job_title", type: "string" },
          { field: "department", as: "department", type: "string" },
          { field: "hiring_manager", as: "hiring_manager", type: "string" },
          { field: "location", as: "location", type: "string" },
          { field: "salary_range", as: "salary_range", type: "string" },
          { field: "experience_years", as: "experience_years", type: "string" },
          { field: "required_skills", as: "required_skills", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "job_id", as: "job_id", type: "string" },
          { field: "urgency", as: "urgency", type: "string" },
        ],
      ),

      // 3. Search for candidates via People API
      n("3", "hitApi", "Search for Sales Manager Candidates", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.apollo.io/v1/mixed_people/search",
          headers: [
            { key: "Content-Type", value: "application/json" },
            { key: "Cache-Control", value: "no-cache" },
          ],
          body: [
            { key: "api_key", value: "{apollo_api_key}" },
            {
              key: "person_titles",
              value:
                '["Sales Manager","Regional Sales Manager","Senior Sales Manager","Sales Director"]',
            },
            { key: "person_locations", value: '["{location}"]' },
            { key: "page", value: "1" },
            { key: "per_page", value: "25" },
          ],
          variables: [
            { name: "people", customName: "candidate_results", type: "string" },
            {
              name: "pagination.total_entries",
              customName: "total_candidates_found",
              type: "number",
            },
            {
              name: "people[0].name",
              customName: "top_candidate_name",
              type: "string",
            },
            {
              name: "people[0].email",
              customName: "top_candidate_email",
              type: "string",
            },
            {
              name: "people[0].title",
              customName: "top_candidate_title",
              type: "string",
            },
            {
              name: "people[0].organization.name",
              customName: "top_candidate_company",
              type: "string",
            },
            {
              name: "people[0].linkedin_url",
              customName: "top_candidate_linkedin",
              type: "string",
            },
          ],
        },
      }),

      // 4. Enrich candidate via Clearbit
      n("4", "hitApi", "Enrich Top Candidate Profile via Clearbit", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://person.clearbit.com/v2/people/find?email={top_candidate_email}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "employment.name",
              customName: "enriched_company",
              type: "string",
            },
            {
              name: "employment.title",
              customName: "enriched_title",
              type: "string",
            },
            {
              name: "employment.seniority",
              customName: "enriched_seniority",
              type: "string",
            },
            {
              name: "location",
              customName: "enriched_location",
              type: "string",
            },
            { name: "bio", customName: "enriched_bio", type: "string" },
            {
              name: "twitter.handle",
              customName: "twitter_handle",
              type: "string",
            },
          ],
        },
      }),

      // 5. AI assess leadership experience
      aiNode(
        "5",
        "AI Assess Leadership and Sales Experience",
        1000,
        "You are a recruiting assessment expert for sales leadership roles. Evaluate the candidate against the job requirements: leadership experience, sales methodology knowledge, team management track record, industry relevance, and cultural fit indicators. Score each dimension 1-10 and provide an overall recommendation. Respond JSON: {leadership_score, sales_methodology_score, team_management_score, industry_relevance_score, overall_score, strengths, concerns, interview_focus_areas, recommendation}",
        "Candidate: {top_candidate_name} | Title: {enriched_title} at {enriched_company} | Seniority: {enriched_seniority} | Bio: {enriched_bio} | Location: {enriched_location} || Job: {job_title} | Required: {experience_years}+ years, Skills: {required_skills} | Location: {location} | Department: {department}",
        [
          { field: "leadership_score", as: "leadership_score", type: "number" },
          {
            field: "overall_score",
            as: "candidate_overall_score",
            type: "number",
          },
          { field: "strengths", as: "candidate_strengths", type: "string" },
          { field: "concerns", as: "candidate_concerns", type: "string" },
          {
            field: "interview_focus_areas",
            as: "interview_focus",
            type: "string",
          },
          { field: "recommendation", as: "ai_recommendation", type: "string" },
        ],
      ),

      // 6. Score and rank candidates runCode
      n("6", "runCode", "Score and Rank Top Candidates", 1240, {
        runCodeDetails: {
          code: `const overallScore = parseInt(variables.candidate_overall_score || '0');
const leadershipScore = parseInt(variables.leadership_score || '0');
const totalFound = parseInt(variables.total_candidates_found || '0');
const urgency = (variables.urgency || '').toLowerCase();
let tierLabel = 'C';
if (overallScore >= 8 && leadershipScore >= 7) tierLabel = 'A';
else if (overallScore >= 6) tierLabel = 'B';
const shouldOutreach = tierLabel === 'A' || (tierLabel === 'B' && urgency === 'high');
const priorityLevel = urgency === 'high' ? 'urgent' : urgency === 'medium' ? 'standard' : 'pipeline';
output.candidate_tier = tierLabel;
output.should_outreach = shouldOutreach;
output.priority_level = priorityLevel;
output.pipeline_summary = 'Job: ' + variables.job_title + ' | Found: ' + totalFound + ' candidates | Top: ' + variables.top_candidate_name + ' (Tier ' + tierLabel + ', Score: ' + overallScore + '/10) | Outreach: ' + (shouldOutreach ? 'Yes' : 'No');`,
          outputVariables: [
            { name: "candidate_tier", type: "string" },
            { name: "should_outreach", type: "boolean" },
            { name: "priority_level", type: "string" },
            { name: "pipeline_summary", type: "string" },
          ],
        },
      }),

      // 7. AI personalized recruiting pitch
      aiNode(
        "7",
        "AI Draft Personalized Recruiting Pitch",
        1480,
        "You are an expert recruiting outreach copywriter. Draft a compelling, personalized recruiting pitch email for the sales manager candidate. Reference their specific experience, highlight how the role aligns with their career trajectory, and include a clear next step (brief call or coffee chat). Keep under 150 words. Respond JSON: {subject_line, email_body, cta_type}",
        "Candidate: {top_candidate_name} | Current: {enriched_title} at {enriched_company} | Strengths: {candidate_strengths} | Tier: {candidate_tier} || Role: {job_title} at {company_name} | Location: {location} | Salary: {salary_range} | Hiring Manager: {hiring_manager} | Recommendation: {ai_recommendation}",
        [
          { field: "subject_line", as: "pitch_subject", type: "string" },
          { field: "email_body", as: "pitch_body", type: "string" },
          { field: "cta_type", as: "pitch_cta", type: "string" },
        ],
      ),

      // 8. Branch by candidate score and send
      n("8", "condition", "Route by Candidate Tier", 1720, {
        conditionDetails: {
          conditions: [
            { variable: "candidate_tier", operator: "equals", value: "A" },
          ],
          logicalOperator: "AND",
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "7", "output_2"),
    ],
  },

  // ===================================================================
  // 1893. SALES NAVIGATOR ENRICHMENT PIPELINE (8 nodes)
  // ===================================================================

  "Sales Navigator Enrichment Pipeline": {
    summary:
      "Fires when a Sales Navigator export arrives via webhook, parses and normalizes lead data with runCode, discovers email addresses via Hunter API, validates email deliverability via ZeroBounce API, AI researches personas, scores lead quality with runCode, syncs to HubSpot CRM, and notifies the SDR via Slack.",
    nodes: [
      trigger("Webhook: Sales Navigator lead export", "webhook_app", {
        source: "linkedin",
        event: "export.completed",
        howItStarts:
          "Fires when a batch of leads is exported from LinkedIn Sales Navigator, providing raw lead profiles that need enrichment, email discovery, validation, and CRM sync before SDR outreach",
        setup:
          "Configure LinkedIn Sales Navigator webhook for export events. Set Hunter.io API key for email discovery. Set ZeroBounce API key for email validation. Set OpenAI API key for persona research. Set HubSpot API token for CRM sync. Configure Slack webhook for SDR notification.",
        inputFields: [],
      }),

      // 2. AI extract + parse lead data
      aiNode(
        "2",
        "AI Extract Sales Navigator Export Data",
        280,
        "You are a LinkedIn data parser. Extract the lead name, title, company, location, industry, company size, LinkedIn URL, connection degree, shared connections count, and any tags from the raw webhook payload. Focus on the first lead in the batch for immediate processing. Respond JSON: {lead_name, lead_title, lead_company, lead_location, lead_industry, company_size, linkedin_url, connection_degree, shared_connections, tags, total_leads_in_export}",
        "Raw webhook payload: {webhook_payload}. Extract all Sales Navigator lead export data regardless of field naming conventions.",
        [
          { field: "lead_name", as: "sn_lead_name", type: "string" },
          { field: "lead_title", as: "sn_lead_title", type: "string" },
          { field: "lead_company", as: "sn_lead_company", type: "string" },
          { field: "lead_location", as: "sn_lead_location", type: "string" },
          { field: "lead_industry", as: "sn_lead_industry", type: "string" },
          { field: "company_size", as: "sn_company_size", type: "string" },
          { field: "linkedin_url", as: "sn_linkedin_url", type: "string" },
          {
            field: "connection_degree",
            as: "sn_connection_degree",
            type: "string",
          },
          {
            field: "shared_connections",
            as: "sn_shared_connections",
            type: "string",
          },
          {
            field: "total_leads_in_export",
            as: "total_export_leads",
            type: "string",
          },
        ],
      ),

      // 3. Parse normalize lead data runCode
      n("3", "runCode", "Parse and Normalize Lead Data", 520, {
        runCodeDetails: {
          code: `const name = (variables.sn_lead_name || '').trim();
const nameParts = name.split(' ');
const firstName = nameParts[0] || '';
const lastName = nameParts.slice(1).join(' ') || '';
const company = (variables.sn_lead_company || '').trim();
const companySlug = company.toLowerCase().replace(/[^a-z0-9]/g, '');
const possibleDomains = [companySlug + '.com', companySlug + '.io', companySlug + '.co'];
const title = (variables.sn_lead_title || '').trim();
const seniorityKeywords = ['vp','director','head','chief','svp','president','founder','cto','cfo','ceo','coo','cmo'];
const isSenior = seniorityKeywords.some(k => title.toLowerCase().includes(k));
output.lead_first_name = firstName;
output.lead_last_name = lastName;
output.lead_company_slug = companySlug;
output.guessed_domain = possibleDomains[0];
output.is_senior_leader = isSenior;
output.normalized_title = title;
output.search_query = firstName + ' ' + lastName + ' ' + company;`,
          outputVariables: [
            { name: "lead_first_name", type: "string" },
            { name: "lead_last_name", type: "string" },
            { name: "lead_company_slug", type: "string" },
            { name: "guessed_domain", type: "string" },
            { name: "is_senior_leader", type: "boolean" },
            { name: "normalized_title", type: "string" },
            { name: "search_query", type: "string" },
          ],
        },
      }),

      // 4. Discover email via Hunter API
      n("4", "hitApi", "Discover Email Address via Hunter", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hunter.io/v2/email-finder?domain={guessed_domain}&first_name={lead_first_name}&last_name={lead_last_name}&api_key={hunter_api_key}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "data.email",
              customName: "discovered_email",
              type: "string",
            },
            {
              name: "data.confidence",
              customName: "email_confidence",
              type: "number",
            },
            {
              name: "data.sources[0].domain",
              customName: "email_source_domain",
              type: "string",
            },
            {
              name: "data.first_name",
              customName: "verified_first_name",
              type: "string",
            },
            {
              name: "data.last_name",
              customName: "verified_last_name",
              type: "string",
            },
          ],
        },
      }),

      // 5. Validate email deliverability via ZeroBounce
      n("5", "hitApi", "Validate Email Deliverability via ZeroBounce", 1000, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.zerobounce.net/v2/validate?api_key={zerobounce_api_key}&email={discovered_email}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            { name: "status", customName: "validation_status", type: "string" },
            {
              name: "sub_status",
              customName: "validation_sub_status",
              type: "string",
            },
            {
              name: "free_email",
              customName: "is_free_email",
              type: "boolean",
            },
            {
              name: "did_you_mean",
              customName: "email_suggestion",
              type: "string",
            },
            { name: "mx_found", customName: "mx_found", type: "string" },
          ],
        },
      }),

      // 6. AI persona research
      aiNode(
        "6",
        "AI Research Lead Persona",
        1240,
        "You are a B2B sales research expert. Based on the lead profile from Sales Navigator, build a comprehensive buyer persona: identify likely pain points for their role and industry, map their probable buying committee position, suggest conversation starters, and recommend the best outreach angle. Respond JSON: {pain_points, buying_role, conversation_starters, outreach_angle, persona_summary, recommended_channel}",
        "Lead: {lead_first_name} {lead_last_name} | Title: {normalized_title} at {sn_lead_company} | Industry: {sn_lead_industry} | Size: {sn_company_size} | Location: {sn_lead_location} | Senior: {is_senior_leader} | Connection: {sn_connection_degree} degree, {sn_shared_connections} shared",
        [
          { field: "pain_points", as: "persona_pain_points", type: "string" },
          { field: "buying_role", as: "persona_buying_role", type: "string" },
          {
            field: "outreach_angle",
            as: "persona_outreach_angle",
            type: "string",
          },
          { field: "persona_summary", as: "persona_summary", type: "string" },
          {
            field: "recommended_channel",
            as: "recommended_channel",
            type: "string",
          },
        ],
      ),

      // 7. Score lead quality runCode
      n("7", "runCode", "Score Lead Quality and Prioritize", 1480, {
        runCodeDetails: {
          code: `let score = 0;
const validationStatus = (variables.validation_status || '').toLowerCase();
const emailConf = parseInt(variables.email_confidence || '0');
const isSenior = variables.is_senior_leader === true || variables.is_senior_leader === 'true';
const connectionDeg = parseInt(variables.sn_connection_degree || '3');
const sharedConns = parseInt(variables.sn_shared_connections || '0');
if (validationStatus === 'valid') score += 25;
else if (validationStatus === 'catch-all') score += 10;
if (emailConf >= 90) score += 20;
else if (emailConf >= 70) score += 10;
if (isSenior) score += 25;
if (connectionDeg <= 2) score += 15;
if (sharedConns >= 5) score += 10;
else if (sharedConns >= 1) score += 5;
const leadGrade = score >= 70 ? 'A' : score >= 45 ? 'B' : 'C';
output.lead_quality_score = score;
output.lead_grade = leadGrade;
output.is_outreach_ready = validationStatus === 'valid' && score >= 45;
output.enrichment_summary = variables.lead_first_name + ' ' + variables.lead_last_name + ' (' + variables.normalized_title + ' at ' + variables.sn_lead_company + ') — Grade: ' + leadGrade + ' (' + score + '/100) | Email: ' + variables.discovered_email + ' (' + validationStatus + ')';`,
          outputVariables: [
            { name: "lead_quality_score", type: "number" },
            { name: "lead_grade", type: "string" },
            { name: "is_outreach_ready", type: "boolean" },
            { name: "enrichment_summary", type: "string" },
          ],
        },
      }),

      // 8. Sync to CRM HubSpot
      hubspotPostNode(
        "8",
        "Sync Enriched Lead to HubSpot CRM",
        1720,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{discovered_email}","firstname":"{lead_first_name}","lastname":"{lead_last_name}","company":"{sn_lead_company}","jobtitle":"{normalized_title}","city":"{sn_lead_location}","industry":"{sn_lead_industry}","numberofemployees":"{sn_company_size}","hs_lead_status":"NEW","lifecyclestage":"lead","lead_source":"Sales Navigator","hs_content_membership_notes":"{persona_summary}. Pain: {persona_pain_points}. Angle: {persona_outreach_angle}. Grade: {lead_grade} ({lead_quality_score}/100)"}',
          },
        ],
        [
          { name: "id", customName: "hubspot_contact_id", type: "string" },
          {
            name: "properties.email",
            customName: "synced_email",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1894. SALESFORCE CONTACT UPSERT (7 nodes)
  // ===================================================================

  "Salesforce Contact Upsert": {
    summary:
      "Fires when contact data arrives via webhook, normalizes it with runCode, checks for existing records in HubSpot, branches by match result, handles duplicate merge with runCode, upserts the contact to HubSpot, and logs the upsert result in Google Sheets.",
    nodes: [
      trigger("Webhook: Contact data for upsert", "webhook_app", {
        source: "salesforce",
        event: "contact.updated",
        howItStarts:
          "Fires when a contact record is created or updated in Salesforce, triggering a sync to HubSpot with deduplication, normalization, and merge handling",
        setup:
          "Configure Salesforce webhook for contact update events. Set HubSpot API token for dedup search and upsert. Set Google Sheets credentials for upsert logging.",
        inputFields: [],
      }),

      // 2. AI extract + normalize contact data
      aiNode(
        "2",
        "AI Extract Contact Data from Salesforce",
        280,
        "You are a Salesforce data parser. Extract the contact first name, last name, email, phone, company (Account name), title, mailing address, lead source, Salesforce contact ID, owner ID, and last modified date from the raw webhook payload. Respond JSON: {first_name, last_name, email, phone, company, title, mailing_address, lead_source, sf_contact_id, sf_owner_id, last_modified}",
        "Raw webhook payload: {webhook_payload}. Extract all Salesforce contact fields regardless of field naming conventions.",
        [
          { field: "first_name", as: "sf_first_name", type: "string" },
          { field: "last_name", as: "sf_last_name", type: "string" },
          { field: "email", as: "sf_email", type: "string" },
          { field: "phone", as: "sf_phone", type: "string" },
          { field: "company", as: "sf_company", type: "string" },
          { field: "title", as: "sf_title", type: "string" },
          { field: "lead_source", as: "sf_lead_source", type: "string" },
          { field: "sf_contact_id", as: "sf_contact_id", type: "string" },
          { field: "last_modified", as: "sf_last_modified", type: "string" },
        ],
      ),

      // 3. Normalize contact data runCode
      n("3", "runCode", "Normalize Contact Data for Upsert", 520, {
        runCodeDetails: {
          code: `const email = (variables.sf_email || '').toLowerCase().trim();
const firstName = (variables.sf_first_name || '').trim();
const lastName = (variables.sf_last_name || '').trim();
const phone = (variables.sf_phone || '').replace(/[^0-9+]/g, '');
const company = (variables.sf_company || '').trim();
const title = (variables.sf_title || '').trim();
const domain = email.split('@')[1] || '';
const isPersonalEmail = ['gmail.com','yahoo.com','hotmail.com','outlook.com','aol.com'].includes(domain);
const hasMinimumData = email.length > 0 && firstName.length > 0 && lastName.length > 0;
output.normalized_email = email;
output.normalized_first = firstName;
output.normalized_last = lastName;
output.normalized_phone = phone;
output.normalized_company = company;
output.normalized_title = title;
output.email_domain = domain;
output.is_personal_email = isPersonalEmail;
output.has_minimum_data = hasMinimumData;
output.upsert_key = email;`,
          outputVariables: [
            { name: "normalized_email", type: "string" },
            { name: "normalized_first", type: "string" },
            { name: "normalized_last", type: "string" },
            { name: "normalized_phone", type: "string" },
            { name: "normalized_company", type: "string" },
            { name: "normalized_title", type: "string" },
            { name: "email_domain", type: "string" },
            { name: "is_personal_email", type: "boolean" },
            { name: "has_minimum_data", type: "boolean" },
            { name: "upsert_key", type: "string" },
          ],
        },
      }),

      // 4. Check for existing records HubSpot
      hubspotPostNode(
        "4",
        "Check for Existing HubSpot Contact",
        760,
        "crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{normalized_email}"}]}]',
          },
          {
            key: "properties",
            value:
              '["email","firstname","lastname","company","jobtitle","phone","hs_lead_status","lifecyclestage","createdate","hs_lastmodifieddate"]',
          },
        ],
        [
          { name: "total", customName: "match_count", type: "number" },
          {
            name: "results[0].id",
            customName: "existing_hubspot_id",
            type: "string",
          },
          {
            name: "results[0].properties.hs_lastmodifieddate",
            customName: "hs_last_modified",
            type: "string",
          },
          {
            name: "results[0].properties.lifecyclestage",
            customName: "existing_lifecycle",
            type: "string",
          },
        ],
      ),

      // 5. Branch by match result
      n("5", "condition", "Route by Existing Contact Match", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "match_count", operator: "gte", value: "1" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 6. Handle duplicate merge runCode
      n("6", "runCode", "Handle Duplicate Merge Strategy", 1240, {
        runCodeDetails: {
          code: `const matchCount = parseInt(variables.match_count || '0');
const existingId = variables.existing_hubspot_id || '';
const sfLastMod = new Date(variables.sf_last_modified || 0).getTime();
const hsLastMod = new Date(variables.hs_last_modified || 0).getTime();
const sfIsNewer = sfLastMod > hsLastMod;
let upsertMethod = 'create';
let mergeFields = {};
if (matchCount > 0 && existingId) {
  upsertMethod = 'update';
  if (sfIsNewer) {
    mergeFields = { firstname: variables.normalized_first, lastname: variables.normalized_last, company: variables.normalized_company, jobtitle: variables.normalized_title, phone: variables.normalized_phone };
  } else {
    mergeFields = { phone: variables.normalized_phone || '', jobtitle: variables.normalized_title || '' };
  }
}
output.upsert_method = upsertMethod;
output.merge_fields_json = JSON.stringify(mergeFields);
output.sf_is_newer = sfIsNewer;
output.target_hubspot_id = existingId || 'new';
output.merge_summary = upsertMethod + ' for ' + variables.normalized_email + ' | SF newer: ' + sfIsNewer + ' | Fields: ' + Object.keys(mergeFields).join(',');`,
          outputVariables: [
            { name: "upsert_method", type: "string" },
            { name: "merge_fields_json", type: "string" },
            { name: "sf_is_newer", type: "boolean" },
            { name: "target_hubspot_id", type: "string" },
            { name: "merge_summary", type: "string" },
          ],
        },
      }),

      // 7. Upsert contact HubSpot
      hubspotPostNode(
        "7",
        "Upsert Contact to HubSpot CRM",
        1480,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{normalized_email}","firstname":"{normalized_first}","lastname":"{normalized_last}","company":"{normalized_company}","jobtitle":"{normalized_title}","phone":"{normalized_phone}","hs_lead_status":"CONNECTED","lead_source":"{sf_lead_source}","salesforce_contact_id":"{sf_contact_id}","notes_last_updated":"SF sync: {upsert_method} on ' +
              new Date().toISOString().split("T")[0] +
              '. {merge_summary}"}',
          },
        ],
        [
          { name: "id", customName: "upserted_hubspot_id", type: "string" },
          {
            name: "properties.email",
            customName: "confirmed_email",
            type: "string",
          },
          {
            name: "properties.lifecyclestage",
            customName: "lifecycle_after_upsert",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("5", "7", "output_2"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1895. SALESFORCE ENRICHMENT BATCH (8 nodes)
  // ===================================================================

  "Salesforce Enrichment Batch": {
    summary:
      "Runs nightly on a schedule, queries unenriched records from HubSpot, batch enriches via Clearbit API, handles API rate limits with runCode, validates and transforms data with runCode, branches by enrichment quality, writes enriched data to HubSpot, and generates an enrichment report in Google Sheets.",
    nodes: [
      trigger("Schedule: Nightly enrichment batch", "schedule", {
        schedule: "Every day at 2:00 AM",
        howItStarts:
          "Runs nightly to identify unenriched CRM records, batch-enrich them via Clearbit, validate the data quality, and write back enriched profiles to the CRM with full reporting",
        setup:
          "Configure HubSpot API token for querying and updating contacts. Set Clearbit API key for company and person enrichment. Set Google Sheets credentials for enrichment reporting.",
        inputFields: [],
      }),

      // 2. Query unenriched records HubSpot
      hubspotPostNode(
        "2",
        "Query Unenriched Records from HubSpot",
        280,
        "crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"industry","operator":"NOT_HAS_PROPERTY"},{"propertyName":"email","operator":"HAS_PROPERTY"},{"propertyName":"lifecyclestage","operator":"IN","values":["lead","marketingqualifiedlead"]}]}]',
          },
          {
            key: "properties",
            value:
              '["email","firstname","lastname","company","jobtitle","createdate"]',
          },
          { key: "limit", value: "100" },
          {
            key: "sorts",
            value: '[{"propertyName":"createdate","direction":"DESCENDING"}]',
          },
        ],
        [
          { name: "results", customName: "unenriched_records", type: "string" },
          { name: "total", customName: "unenriched_total", type: "number" },
        ],
      ),

      // 3. Batch enrich via Clearbit API
      n("3", "hitApi", "Batch Enrich via Clearbit Company API", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://company.clearbit.com/v2/companies/find?domain={unenriched_records}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "name",
              customName: "enriched_company_name",
              type: "string",
            },
            {
              name: "metrics.employees",
              customName: "enriched_employees",
              type: "string",
            },
            {
              name: "metrics.annualRevenue",
              customName: "enriched_revenue",
              type: "string",
            },
            {
              name: "category.industry",
              customName: "enriched_industry",
              type: "string",
            },
            {
              name: "category.subIndustry",
              customName: "enriched_sub_industry",
              type: "string",
            },
            {
              name: "geo.country",
              customName: "enriched_country",
              type: "string",
            },
            { name: "tech", customName: "enriched_tech_stack", type: "string" },
            {
              name: "metrics.raised",
              customName: "enriched_funding",
              type: "string",
            },
          ],
        },
      }),

      // 4. Handle API rate limits runCode
      n("4", "runCode", "Handle API Rate Limits and Batch Progress", 760, {
        runCodeDetails: {
          code: `const totalRecords = parseInt(variables.unenriched_total || '0');
const batchSize = 100;
const apiRateLimit = 600;
const estimatedBatches = Math.ceil(totalRecords / batchSize);
const estimatedMinutes = Math.ceil(totalRecords / apiRateLimit);
const hasEnrichmentData = (variables.enriched_company_name || '').length > 0;
const enrichmentSuccess = hasEnrichmentData ? 1 : 0;
const batchNumber = 1;
output.batch_number = batchNumber;
output.total_batches = estimatedBatches;
output.estimated_completion_minutes = estimatedMinutes;
output.enrichment_success = enrichmentSuccess;
output.records_remaining = Math.max(0, totalRecords - batchSize);
output.rate_limit_status = totalRecords <= apiRateLimit ? 'within_limits' : 'throttle_required';
output.batch_progress = 'Batch ' + batchNumber + '/' + estimatedBatches + ' | ' + totalRecords + ' total records | Est. ' + estimatedMinutes + ' min';`,
          outputVariables: [
            { name: "batch_number", type: "number" },
            { name: "total_batches", type: "number" },
            { name: "estimated_completion_minutes", type: "number" },
            { name: "enrichment_success", type: "number" },
            { name: "records_remaining", type: "number" },
            { name: "rate_limit_status", type: "string" },
            { name: "batch_progress", type: "string" },
          ],
        },
      }),

      // 5. Validate transform data runCode
      n("5", "runCode", "Validate and Transform Enriched Data", 1000, {
        runCodeDetails: {
          code: `const industry = (variables.enriched_industry || '').trim();
const employees = variables.enriched_employees || '';
const revenue = variables.enriched_revenue || '';
const country = (variables.enriched_country || '').trim();
const companyName = (variables.enriched_company_name || '').trim();
let qualityScore = 0;
const totalFields = 6;
let filledFields = 0;
if (industry.length > 0) { filledFields++; qualityScore += 20; }
if (employees.length > 0 && employees !== '0') { filledFields++; qualityScore += 20; }
if (revenue.length > 0 && revenue !== '0') { filledFields++; qualityScore += 20; }
if (country.length > 0) { filledFields++; qualityScore += 15; }
if (companyName.length > 0) { filledFields++; qualityScore += 15; }
if ((variables.enriched_tech_stack || '').length > 0) { filledFields++; qualityScore += 10; }
const enrichmentQuality = qualityScore >= 70 ? 'high' : qualityScore >= 40 ? 'medium' : 'low';
const completeness = Math.round((filledFields / totalFields) * 100);
output.enrichment_quality = enrichmentQuality;
output.quality_score = qualityScore;
output.completeness_pct = completeness;
output.filled_fields = filledFields;
output.validation_summary = companyName + ': ' + enrichmentQuality + ' quality (' + qualityScore + '/100, ' + completeness + '% complete). Industry: ' + industry + ', Employees: ' + employees;`,
          outputVariables: [
            { name: "enrichment_quality", type: "string" },
            { name: "quality_score", type: "number" },
            { name: "completeness_pct", type: "number" },
            { name: "filled_fields", type: "number" },
            { name: "validation_summary", type: "string" },
          ],
        },
      }),

      // 6. Branch by enrichment quality
      n("6", "condition", "Route by Enrichment Quality Level", 1240, {
        conditionDetails: {
          conditions: [
            {
              variable: "enrichment_quality",
              operator: "equals",
              value: "high",
            },
          ],
          logicalOperator: "AND",
        },
      }),

      // 7. Write enriched data HubSpot
      hubspotPostNode(
        "7",
        "Write Enriched Data to HubSpot",
        1480,
        "crm/v3/objects/contacts/batch/update",
        [
          {
            key: "inputs",
            value:
              '[{"properties":{"industry":"{enriched_industry}","numberofemployees":"{enriched_employees}","annualrevenue":"{enriched_revenue}","country":"{enriched_country}","company":"{enriched_company_name}","notes_last_updated":"Enriched batch {batch_number}: Quality {enrichment_quality} ({quality_score}/100). {validation_summary}"}}]',
          },
        ],
        [
          { name: "status", customName: "update_status", type: "string" },
          {
            name: "results[0].id",
            customName: "updated_contact_id",
            type: "string",
          },
        ],
      ),

      // 8. Generate enrichment report Sheets
      sheetsNode(
        "8",
        "Generate Enrichment Report in Sheets",
        1720,
        "EnrichmentBatch",
        '[["{batch_progress}","{unenriched_total}","{enrichment_quality}","{quality_score}","{completeness_pct}%","{enriched_industry}","{enriched_employees}","{enriched_revenue}","{rate_limit_status}","' +
          new Date().toISOString().split("T")[0] +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("6", "7", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1896. SCHOOL DISTRICT RESEARCH PIPELINE (9 nodes)
  // ===================================================================

  "School District Research Pipeline": {
    summary:
      "Starts manually for district research, fetches district demographics with runCode, researches district budget with runCode, assesses technology adoption with runCode, identifies key administrators via API, discovers administrator contacts via API, AI generates district profile briefs, branches by district size, and syncs district profiles to HubSpot CRM.",
    nodes: [
      trigger("Manual: School district research campaign", "manual", {
        howItStarts:
          "Triggered manually when targeting a school district for EdTech outreach, providing the district name, state, and product focus area for comprehensive research and stakeholder discovery",
        setup:
          "Configure OpenAI API key for AI analysis. Set Apollo/Hunter API keys for contact discovery. Set HubSpot API token for CRM sync.",
        inputFields: [
          {
            name: "district_name",
            type: "string",
            example: "Los Angeles Unified School District",
          },
          { name: "district_state", type: "string", example: "California" },
          {
            name: "product_focus",
            type: "string",
            example: "Student engagement platform",
          },
          { name: "enrollment_target", type: "string", example: "10000" },
        ],
      }),

      // 2. Fetch district demographics runCode
      n("2", "runCode", "Compile District Demographics Profile", 280, {
        runCodeDetails: {
          code: `const districtName = variables.district_name || '';
const state = variables.district_state || '';
const enrollmentTarget = parseInt(variables.enrollment_target || '0');
const districtSlug = districtName.toLowerCase().replace(/[^a-z0-9]/g, '-');
const stateAbbreviations = { 'california': 'CA', 'texas': 'TX', 'new york': 'NY', 'florida': 'FL', 'illinois': 'IL', 'pennsylvania': 'PA', 'ohio': 'OH', 'georgia': 'GA', 'north carolina': 'NC', 'michigan': 'MI' };
const stateAbbr = stateAbbreviations[state.toLowerCase()] || state.substring(0, 2).toUpperCase();
const districtSizeCategory = enrollmentTarget >= 50000 ? 'large' : enrollmentTarget >= 10000 ? 'medium' : enrollmentTarget >= 2000 ? 'small' : 'rural';
const estimatedSchools = Math.ceil(enrollmentTarget / 500);
const estimatedTeachers = Math.ceil(enrollmentTarget / 15);
output.district_slug = districtSlug;
output.state_abbr = stateAbbr;
output.district_size_category = districtSizeCategory;
output.estimated_schools = estimatedSchools;
output.estimated_teachers = estimatedTeachers;
output.nces_search_url = 'https://nces.ed.gov/ccd/districtsearch/?query=' + encodeURIComponent(districtName) + '&state=' + stateAbbr;
output.demographics_summary = districtName + ', ' + stateAbbr + ': ' + districtSizeCategory + ' district (~' + enrollmentTarget + ' students, ~' + estimatedSchools + ' schools, ~' + estimatedTeachers + ' teachers)';`,
          outputVariables: [
            { name: "district_slug", type: "string" },
            { name: "state_abbr", type: "string" },
            { name: "district_size_category", type: "string" },
            { name: "estimated_schools", type: "number" },
            { name: "estimated_teachers", type: "number" },
            { name: "nces_search_url", type: "string" },
            { name: "demographics_summary", type: "string" },
          ],
        },
      }),

      // 3. Research district budget runCode
      n("3", "runCode", "Research District Budget Estimates", 520, {
        runCodeDetails: {
          code: `const enrollment = parseInt(variables.enrollment_target || '0');
const sizeCategory = variables.district_size_category || 'medium';
const avgPerPupilSpend = { 'large': 14000, 'medium': 12500, 'small': 11000, 'rural': 10000 };
const techSpendPct = { 'large': 0.04, 'medium': 0.035, 'small': 0.03, 'rural': 0.025 };
const perPupil = avgPerPupilSpend[sizeCategory] || 12000;
const techPct = techSpendPct[sizeCategory] || 0.03;
const totalBudget = enrollment * perPupil;
const techBudget = totalBudget * techPct;
const estimatedEdTechBudget = techBudget * 0.3;
const fiscalYear = new Date().getFullYear() + (new Date().getMonth() >= 6 ? 1 : 0);
const budgetCyclePhase = new Date().getMonth() >= 1 && new Date().getMonth() <= 4 ? 'planning' : new Date().getMonth() >= 5 && new Date().getMonth() <= 7 ? 'approval' : 'execution';
output.estimated_total_budget = Math.round(totalBudget);
output.estimated_tech_budget = Math.round(techBudget);
output.estimated_edtech_budget = Math.round(estimatedEdTechBudget);
output.per_pupil_spending = perPupil;
output.fiscal_year = fiscalYear;
output.budget_cycle_phase = budgetCyclePhase;
output.budget_summary = '$' + Math.round(totalBudget / 1000000) + 'M total | $' + Math.round(techBudget / 1000) + 'K tech | $' + Math.round(estimatedEdTechBudget / 1000) + 'K EdTech | FY' + fiscalYear + ' (' + budgetCyclePhase + ')';`,
          outputVariables: [
            { name: "estimated_total_budget", type: "number" },
            { name: "estimated_tech_budget", type: "number" },
            { name: "estimated_edtech_budget", type: "number" },
            { name: "per_pupil_spending", type: "number" },
            { name: "fiscal_year", type: "number" },
            { name: "budget_cycle_phase", type: "string" },
            { name: "budget_summary", type: "string" },
          ],
        },
      }),

      // 4. Assess technology adoption runCode
      n("4", "runCode", "Assess District Technology Adoption Level", 760, {
        runCodeDetails: {
          code: `const sizeCategory = variables.district_size_category || 'medium';
const techBudget = parseInt(variables.estimated_tech_budget || '0');
const enrollment = parseInt(variables.enrollment_target || '0');
const techPerStudent = enrollment > 0 ? techBudget / enrollment : 0;
let adoptionLevel = 'moderate';
let adoptionIndicators = [];
if (techPerStudent >= 600) { adoptionLevel = 'advanced'; adoptionIndicators.push('High per-student tech spend'); }
else if (techPerStudent >= 400) { adoptionLevel = 'moderate'; adoptionIndicators.push('Average per-student tech spend'); }
else { adoptionLevel = 'emerging'; adoptionIndicators.push('Below-average tech spend'); }
if (sizeCategory === 'large') adoptionIndicators.push('Large district — likely dedicated IT team');
else if (sizeCategory === 'rural') adoptionIndicators.push('Rural — may have infrastructure challenges');
const readinessScore = adoptionLevel === 'advanced' ? 85 : adoptionLevel === 'moderate' ? 60 : 35;
output.tech_adoption_level = adoptionLevel;
output.tech_per_student = Math.round(techPerStudent);
output.adoption_indicators = adoptionIndicators.join('; ');
output.readiness_score = readinessScore;
output.adoption_summary = adoptionLevel + ' adoption ($' + Math.round(techPerStudent) + '/student) — ' + adoptionIndicators.join(', ');`,
          outputVariables: [
            { name: "tech_adoption_level", type: "string" },
            { name: "tech_per_student", type: "number" },
            { name: "adoption_indicators", type: "string" },
            { name: "readiness_score", type: "number" },
            { name: "adoption_summary", type: "string" },
          ],
        },
      }),

      // 5. Identify key administrators via Apollo API
      n("5", "hitApi", "Identify Key District Administrators", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.apollo.io/v1/mixed_people/search",
          headers: [
            { key: "Content-Type", value: "application/json" },
            { key: "Cache-Control", value: "no-cache" },
          ],
          body: [
            { key: "api_key", value: "{apollo_api_key}" },
            {
              key: "person_titles",
              value:
                '["Superintendent","CTO","Chief Technology Officer","Director of Technology","IT Director","Curriculum Director","Assistant Superintendent"]',
            },
            { key: "q_organization_name", value: "{district_name}" },
            { key: "per_page", value: "10" },
          ],
          variables: [
            {
              name: "people[0].name",
              customName: "admin1_name",
              type: "string",
            },
            {
              name: "people[0].title",
              customName: "admin1_title",
              type: "string",
            },
            {
              name: "people[0].email",
              customName: "admin1_email",
              type: "string",
            },
            {
              name: "people[0].linkedin_url",
              customName: "admin1_linkedin",
              type: "string",
            },
            {
              name: "people[1].name",
              customName: "admin2_name",
              type: "string",
            },
            {
              name: "people[1].title",
              customName: "admin2_title",
              type: "string",
            },
            {
              name: "people[1].email",
              customName: "admin2_email",
              type: "string",
            },
            {
              name: "pagination.total_entries",
              customName: "total_admins_found",
              type: "number",
            },
          ],
        },
      }),

      // 6. Discover administrator contacts via Hunter
      n("6", "hitApi", "Discover Administrator Contact Emails", 1240, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hunter.io/v2/email-verifier?email={admin1_email}&api_key={hunter_api_key}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "data.status",
              customName: "admin1_email_status",
              type: "string",
            },
            {
              name: "data.score",
              customName: "admin1_email_score",
              type: "number",
            },
            {
              name: "data.result",
              customName: "admin1_verify_result",
              type: "string",
            },
          ],
        },
      }),

      // 7. AI generate district profile briefs
      aiNode(
        "7",
        "AI Generate District Profile Brief",
        1480,
        "You are an EdTech sales strategist. Create a comprehensive district profile brief for the sales team including: district overview, budget timing, technology readiness assessment, key stakeholders, recommended engagement strategy, potential objections, and suggested pilot proposal. Respond JSON: {district_overview, engagement_strategy, potential_objections, pilot_proposal, key_talking_points, recommended_first_contact, timing_recommendation}",
        "District: {district_name}, {state_abbr} | Size: {district_size_category} ({enrollment_target} students, ~{estimated_schools} schools) | Budget: {budget_summary} | Tech: {adoption_summary} | Readiness: {readiness_score}/100 | Budget Phase: {budget_cycle_phase} | Key Admin: {admin1_name} ({admin1_title}) — Email: {admin1_email} ({admin1_email_status}) | Admin 2: {admin2_name} ({admin2_title}) | Product: {product_focus}",
        [
          {
            field: "district_overview",
            as: "district_overview",
            type: "string",
          },
          {
            field: "engagement_strategy",
            as: "engagement_strategy",
            type: "string",
          },
          {
            field: "potential_objections",
            as: "potential_objections",
            type: "string",
          },
          { field: "pilot_proposal", as: "pilot_proposal", type: "string" },
          { field: "key_talking_points", as: "talking_points", type: "string" },
          {
            field: "recommended_first_contact",
            as: "first_contact_rec",
            type: "string",
          },
          { field: "timing_recommendation", as: "timing_rec", type: "string" },
        ],
      ),

      // 8. Branch by district size
      n("8", "condition", "Route by District Size Category", 1720, {
        conditionDetails: {
          conditions: [
            {
              variable: "district_size_category",
              operator: "equals",
              value: "large",
            },
          ],
          logicalOperator: "AND",
        },
      }),

      // 9. Sync district profiles to CRM HubSpot
      hubspotPostNode(
        "9",
        "Sync District Profile to HubSpot CRM",
        1960,
        "crm/v3/objects/companies",
        [
          {
            key: "properties",
            value:
              '{"name":"{district_name}","state":"{state_abbr}","industry":"Education","numberofemployees":"{estimated_teachers}","annualrevenue":"{estimated_total_budget}","description":"{district_overview}","notes_last_updated":"EdTech Research: {demographics_summary}. Budget: {budget_summary}. Tech: {adoption_summary}. Strategy: {engagement_strategy}. First Contact: {first_contact_rec}. Timing: {timing_rec}","hs_lead_status":"NEW"}',
          },
        ],
        [
          { name: "id", customName: "hubspot_company_id", type: "string" },
          {
            name: "properties.name",
            customName: "confirmed_district",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
      e("8", "9", "output_2"),
    ],
  },

  // ===================================================================
  // 1897. SECURITY REVIEW STAKEHOLDER MAP (9 nodes)
  // ===================================================================

  "Security Review Stakeholder Map": {
    summary:
      "Fires when a security review is initiated via webhook, parses security requirements with runCode, identifies security stakeholders via API, enriches stakeholder profiles via Clearbit, AI prepares questionnaire responses, AI creates stakeholder-specific engagement plans, branches by security complexity, sends the security package email, and creates a security review tracker in HubSpot.",
    nodes: [
      trigger("Webhook: Security review initiated", "webhook_app", {
        source: "hubspot",
        event: "deal.propertyChange",
        howItStarts:
          "Fires when a deal enters the security review stage, triggering automated stakeholder mapping, questionnaire preparation, and engagement planning to accelerate the security review process",
        setup:
          "Configure HubSpot webhook for deal stage changes to security review. Set Apollo API key for stakeholder identification. Set Clearbit API key for profile enrichment. Set OpenAI API key for questionnaire and engagement planning. Configure email sending credentials.",
        inputFields: [],
      }),

      // 2. AI extract security review data
      aiNode(
        "2",
        "AI Extract Security Review Details",
        280,
        "You are a security review data parser. Extract the deal name, company name, deal amount, deal stage, security requirements list, compliance frameworks needed (SOC2/ISO/HIPAA), review deadline, prospect contact email, prospect name, and any specific security concerns from the raw webhook payload. Respond JSON: {deal_name, prospect_company, deal_amount, deal_stage, security_requirements, compliance_frameworks, review_deadline, prospect_email, prospect_name, security_concerns}",
        "Raw webhook payload: {webhook_payload}. Extract all security review initiation data regardless of field naming conventions.",
        [
          { field: "deal_name", as: "deal_name", type: "string" },
          { field: "prospect_company", as: "prospect_company", type: "string" },
          { field: "deal_amount", as: "deal_amount", type: "string" },
          {
            field: "security_requirements",
            as: "security_requirements",
            type: "string",
          },
          {
            field: "compliance_frameworks",
            as: "compliance_frameworks",
            type: "string",
          },
          { field: "review_deadline", as: "review_deadline", type: "string" },
          { field: "prospect_email", as: "prospect_email", type: "string" },
          { field: "prospect_name", as: "prospect_name", type: "string" },
          {
            field: "security_concerns",
            as: "security_concerns",
            type: "string",
          },
        ],
      ),

      // 3. Parse security requirements runCode
      n("3", "runCode", "Parse and Classify Security Requirements", 520, {
        runCodeDetails: {
          code: `const requirements = (variables.security_requirements || '').toLowerCase();
const frameworks = (variables.compliance_frameworks || '').toLowerCase();
const concerns = (variables.security_concerns || '').toLowerCase();
const deadline = variables.review_deadline || '';
const isSOC2 = frameworks.includes('soc2') || frameworks.includes('soc 2');
const isISO = frameworks.includes('iso') || frameworks.includes('27001');
const isHIPAA = frameworks.includes('hipaa');
const isGDPR = frameworks.includes('gdpr');
const isPCI = frameworks.includes('pci');
const frameworkCount = [isSOC2, isISO, isHIPAA, isGDPR, isPCI].filter(Boolean).length;
const hasDataResidency = requirements.includes('data residency') || requirements.includes('data location');
const hasPenTest = requirements.includes('pen test') || requirements.includes('penetration');
const hasVendorAssessment = requirements.includes('vendor assessment') || requirements.includes('third party');
const complexityScore = frameworkCount * 20 + (hasDataResidency ? 15 : 0) + (hasPenTest ? 15 : 0) + (hasVendorAssessment ? 10 : 0);
const complexityLevel = complexityScore >= 60 ? 'high' : complexityScore >= 30 ? 'medium' : 'low';
const daysToDeadline = deadline ? Math.ceil((new Date(deadline).getTime() - Date.now()) / 86400000) : 30;
output.complexity_level = complexityLevel;
output.complexity_score = complexityScore;
output.framework_count = frameworkCount;
output.frameworks_list = [isSOC2 && 'SOC2', isISO && 'ISO27001', isHIPAA && 'HIPAA', isGDPR && 'GDPR', isPCI && 'PCI-DSS'].filter(Boolean).join(', ');
output.days_to_deadline = daysToDeadline;
output.has_pen_test_requirement = hasPenTest;
output.requirements_summary = complexityLevel + ' complexity (' + complexityScore + '/100) | Frameworks: ' + frameworkCount + ' | Deadline: ' + daysToDeadline + ' days | PenTest: ' + hasPenTest;`,
          outputVariables: [
            { name: "complexity_level", type: "string" },
            { name: "complexity_score", type: "number" },
            { name: "framework_count", type: "number" },
            { name: "frameworks_list", type: "string" },
            { name: "days_to_deadline", type: "number" },
            { name: "has_pen_test_requirement", type: "boolean" },
            { name: "requirements_summary", type: "string" },
          ],
        },
      }),

      // 4. Identify security stakeholders via Apollo
      n("4", "hitApi", "Identify Security Stakeholders at Prospect", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.apollo.io/v1/mixed_people/search",
          headers: [
            { key: "Content-Type", value: "application/json" },
            { key: "Cache-Control", value: "no-cache" },
          ],
          body: [
            { key: "api_key", value: "{apollo_api_key}" },
            {
              key: "person_titles",
              value:
                '["CISO","Chief Information Security Officer","VP Security","Director of Security","IT Security Manager","Security Engineer","Head of InfoSec","Compliance Manager","Data Protection Officer"]',
            },
            { key: "q_organization_name", value: "{prospect_company}" },
            { key: "per_page", value: "10" },
          ],
          variables: [
            {
              name: "people[0].name",
              customName: "security_lead_name",
              type: "string",
            },
            {
              name: "people[0].title",
              customName: "security_lead_title",
              type: "string",
            },
            {
              name: "people[0].email",
              customName: "security_lead_email",
              type: "string",
            },
            {
              name: "people[0].linkedin_url",
              customName: "security_lead_linkedin",
              type: "string",
            },
            {
              name: "people[1].name",
              customName: "security_contact2_name",
              type: "string",
            },
            {
              name: "people[1].title",
              customName: "security_contact2_title",
              type: "string",
            },
            {
              name: "pagination.total_entries",
              customName: "total_security_contacts",
              type: "number",
            },
          ],
        },
      }),

      // 5. Enrich stakeholder profiles via Clearbit
      n("5", "hitApi", "Enrich Security Stakeholder via Clearbit", 1000, {
        hitApiDetails: {
          method: "GET",
          url: "https://person.clearbit.com/v2/people/find?email={security_lead_email}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "employment.seniority",
              customName: "stakeholder_seniority",
              type: "string",
            },
            { name: "bio", customName: "stakeholder_bio", type: "string" },
            {
              name: "employment.name",
              customName: "confirmed_company",
              type: "string",
            },
            {
              name: "twitter.handle",
              customName: "stakeholder_twitter",
              type: "string",
            },
          ],
        },
      }),

      // 6. AI prepare questionnaire responses
      aiNode(
        "6",
        "AI Prepare Security Questionnaire Responses",
        1240,
        "You are a security compliance expert. Based on the compliance frameworks and security requirements, prepare pre-filled questionnaire responses covering: data encryption (at rest and in transit), access controls, incident response procedures, data retention policies, subprocessor list, and audit trail capabilities. Respond JSON: {encryption_response, access_control_response, incident_response, data_retention_response, subprocessor_response, audit_trail_response, additional_certifications, questionnaire_confidence}",
        "Frameworks: {frameworks_list} | Requirements: {security_requirements} | Concerns: {security_concerns} | Complexity: {complexity_level} ({complexity_score}/100) | PenTest Needed: {has_pen_test_requirement} | Deadline: {days_to_deadline} days",
        [
          {
            field: "encryption_response",
            as: "encryption_answer",
            type: "string",
          },
          {
            field: "access_control_response",
            as: "access_control_answer",
            type: "string",
          },
          {
            field: "incident_response",
            as: "incident_response_answer",
            type: "string",
          },
          {
            field: "questionnaire_confidence",
            as: "questionnaire_confidence",
            type: "string",
          },
        ],
      ),

      // 7. AI stakeholder-specific engagement
      aiNode(
        "7",
        "AI Create Stakeholder Engagement Plan",
        1480,
        "You are an enterprise security review strategist. Create a stakeholder-specific engagement plan for accelerating the security review. Include: recommended meeting agenda, key documents to proactively share, potential blockers to address preemptively, and a communication cadence. Respond JSON: {meeting_agenda, documents_to_share, potential_blockers, communication_cadence, engagement_summary, recommended_approach}",
        "Security Lead: {security_lead_name} ({security_lead_title}) | Seniority: {stakeholder_seniority} | Bio: {stakeholder_bio} | Additional Contact: {security_contact2_name} ({security_contact2_title}) | Frameworks: {frameworks_list} | Complexity: {complexity_level} | Deal: {deal_name} (${deal_amount}) | Deadline: {days_to_deadline} days | Questionnaire Confidence: {questionnaire_confidence}",
        [
          { field: "meeting_agenda", as: "meeting_agenda", type: "string" },
          { field: "documents_to_share", as: "docs_to_share", type: "string" },
          {
            field: "potential_blockers",
            as: "potential_blockers",
            type: "string",
          },
          {
            field: "engagement_summary",
            as: "engagement_summary",
            type: "string",
          },
          {
            field: "recommended_approach",
            as: "recommended_approach",
            type: "string",
          },
        ],
      ),

      // 8. Branch by security complexity
      n("8", "condition", "Route by Security Complexity Level", 1720, {
        conditionDetails: {
          conditions: [
            { variable: "complexity_level", operator: "equals", value: "high" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 9. Send security package email
      emailNode(
        "9",
        "Send Security Review Package Email",
        1960,
        "{security_lead_email}",
        "Security Review Package — {deal_name} | {frameworks_list} Compliance",
        "Hi {security_lead_name},\\n\\nThank you for initiating the security review for {deal_name}. We have proactively prepared the following to expedite the process:\\n\\nCompliance Frameworks: {frameworks_list}\\n\\nKey Responses Prepared:\\n- Encryption: {encryption_answer}\\n- Access Controls: {access_control_answer}\\n- Incident Response: {incident_response_answer}\\n\\nDocuments to Share: {docs_to_share}\\n\\nSuggested Next Steps: {recommended_approach}\\n\\nMeeting Agenda Proposal: {meeting_agenda}\\n\\nWe are targeting completion within {days_to_deadline} days. Please let us know the best time to schedule a walkthrough.\\n\\nBest regards",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
      e("8", "9", "output_2"),
    ],
  },

  // ===================================================================
  // 1898. SECURITY REVIEW SUBMISSION (7 nodes)
  // ===================================================================

  "Security Review Submission": {
    summary:
      "Fires when a security review need is identified via webhook, fetches company security posture with runCode, AI generates a compliance summary, AI customizes for the prospect industry, branches by compliance requirement level, sends a proactive security package email, and logs the security submission to HubSpot CRM.",
    nodes: [
      trigger("Webhook: Security review need identified", "webhook_app", {
        source: "hubspot",
        event: "deal.propertyChange",
        howItStarts:
          "Fires when a deal is flagged as needing a security review, triggering automated compilation of the company security posture, compliance documentation, and proactive outreach to the prospect security team",
        setup:
          "Configure HubSpot webhook for deal property changes on security review flags. Set OpenAI API key for compliance summaries. Configure email sending credentials. Set HubSpot API token for submission logging.",
        inputFields: [],
      }),

      // 2. AI extract security need data
      aiNode(
        "2",
        "AI Extract Security Review Need",
        280,
        "You are a security review request parser. Extract the deal ID, deal name, prospect company, prospect industry, compliance requirements (SOC2/HIPAA/ISO/GDPR), security contact email, security contact name, deal value, and urgency level from the raw webhook payload. Respond JSON: {deal_id, deal_name, prospect_company, prospect_industry, compliance_requirements, security_contact_email, security_contact_name, deal_value, urgency_level}",
        "Raw webhook payload: {webhook_payload}. Extract all security review need data regardless of field naming conventions.",
        [
          { field: "deal_id", as: "deal_id", type: "string" },
          { field: "deal_name", as: "deal_name", type: "string" },
          { field: "prospect_company", as: "prospect_company", type: "string" },
          {
            field: "prospect_industry",
            as: "prospect_industry",
            type: "string",
          },
          {
            field: "compliance_requirements",
            as: "compliance_reqs",
            type: "string",
          },
          {
            field: "security_contact_email",
            as: "security_email",
            type: "string",
          },
          {
            field: "security_contact_name",
            as: "security_name",
            type: "string",
          },
          { field: "deal_value", as: "deal_value", type: "string" },
          { field: "urgency_level", as: "urgency", type: "string" },
        ],
      ),

      // 3. Fetch company security posture runCode
      n("3", "runCode", "Compile Company Security Posture", 520, {
        runCodeDetails: {
          code: `const reqs = (variables.compliance_reqs || '').toLowerCase();
const hasSOC2 = reqs.includes('soc2') || reqs.includes('soc 2');
const hasHIPAA = reqs.includes('hipaa');
const hasISO = reqs.includes('iso') || reqs.includes('27001');
const hasGDPR = reqs.includes('gdpr');
const certifications = [];
if (hasSOC2) certifications.push('SOC 2 Type II');
if (hasISO) certifications.push('ISO 27001');
if (hasHIPAA) certifications.push('HIPAA BAA Available');
if (hasGDPR) certifications.push('GDPR Compliant');
const securityFeatures = [
  'AES-256 encryption at rest',
  'TLS 1.3 encryption in transit',
  'SSO/SAML 2.0 authentication',
  'Role-based access control (RBAC)',
  '99.9% SLA uptime guarantee',
  'SOC 2 audited annually',
  'Penetration testing quarterly',
  'Data retention configurable',
  '24/7 security monitoring',
];
const complianceLevel = certifications.length >= 3 ? 'enterprise' : certifications.length >= 2 ? 'advanced' : 'standard';
output.certifications_list = certifications.join(', ');
output.security_features = securityFeatures.join('; ');
output.compliance_level = complianceLevel;
output.frameworks_matched = certifications.length;
output.posture_summary = complianceLevel + ' security posture | Certs: ' + certifications.join(', ') + ' | ' + securityFeatures.length + ' security features documented';`,
          outputVariables: [
            { name: "certifications_list", type: "string" },
            { name: "security_features", type: "string" },
            { name: "compliance_level", type: "string" },
            { name: "frameworks_matched", type: "number" },
            { name: "posture_summary", type: "string" },
          ],
        },
      }),

      // 4. AI compliance summary
      aiNode(
        "4",
        "AI Generate Compliance Summary",
        760,
        "You are a security compliance documentation specialist. Generate a concise executive-level compliance summary covering how the company meets the required frameworks, key security controls in place, and areas of strength. Keep it professional and factual. Respond JSON: {executive_summary, key_controls, strengths_highlights, gap_acknowledgments, next_steps_recommendation}",
        "Required Frameworks: {compliance_reqs} | Certifications: {certifications_list} | Security Features: {security_features} | Compliance Level: {compliance_level} | Deal: {deal_name} for {prospect_company}",
        [
          { field: "executive_summary", as: "exec_summary", type: "string" },
          { field: "key_controls", as: "key_controls", type: "string" },
          { field: "strengths_highlights", as: "strengths", type: "string" },
          {
            field: "next_steps_recommendation",
            as: "next_steps",
            type: "string",
          },
        ],
      ),

      // 5. AI customize for prospect industry
      aiNode(
        "5",
        "AI Customize for Prospect Industry",
        1000,
        "You are an industry-specific compliance advisor. Customize the security summary for the prospect industry, addressing industry-specific regulations, common concerns, and relevant case studies. Include industry-specific language and compliance references. Respond JSON: {industry_customization, regulatory_highlights, industry_specific_concerns, tailored_value_prop, recommended_documents}",
        "Prospect Industry: {prospect_industry} | Company: {prospect_company} | Compliance Summary: {exec_summary} | Key Controls: {key_controls} | Strengths: {strengths} | Required: {compliance_reqs}",
        [
          {
            field: "industry_customization",
            as: "industry_custom",
            type: "string",
          },
          {
            field: "regulatory_highlights",
            as: "regulatory_highlights",
            type: "string",
          },
          {
            field: "tailored_value_prop",
            as: "tailored_value_prop",
            type: "string",
          },
          {
            field: "recommended_documents",
            as: "recommended_docs",
            type: "string",
          },
        ],
      ),

      // 6. Branch by compliance requirement level
      n("6", "condition", "Route by Compliance Requirement Level", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "frameworks_matched", operator: "gte", value: "3" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 7. Send proactive security package email
      emailNode(
        "7",
        "Send Proactive Security Package Email",
        1480,
        "{security_email}",
        "Proactive Security Documentation — {deal_name} | {certifications_list}",
        "Hi {security_name},\\n\\nWe understand that security review is a critical step in your evaluation of {deal_name}. To accelerate the process, we have proactively compiled our security documentation package:\\n\\nExecutive Summary:\\n{exec_summary}\\n\\nKey Security Controls:\\n{key_controls}\\n\\nIndustry-Specific Compliance ({prospect_industry}):\\n{industry_custom}\\n\\nRegulatory Highlights:\\n{regulatory_highlights}\\n\\nCertifications: {certifications_list}\\n\\nRecommended Next Steps:\\n{next_steps}\\n\\nRecommended Documents for Review:\\n{recommended_docs}\\n\\nWe are available for a security deep-dive call at your convenience.\\n\\nBest regards",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("6", "7", "output_2"),
    ],
  },

  // ===================================================================
  // 1899. SENDER DOMAIN AUTH ALERT (8 nodes)
  // ===================================================================

  "Sender Domain Auth Alert": {
    summary:
      "Runs on a schedule to fetch active sending domains with runCode, checks SPF records with runCode, checks DKIM records with runCode, checks DMARC records with runCode, analyzes overall authentication health with runCode, branches by severity level, and alerts the operations team via Slack.",
    nodes: [
      trigger("Schedule: Daily domain auth check", "schedule", {
        schedule: "Every day at 8:00 AM",
        howItStarts:
          "Runs daily to audit all active sending domains for proper SPF, DKIM, and DMARC authentication configuration, alerting operations when authentication records are missing, misconfigured, or about to expire",
        setup:
          "Configure Slack webhook for operations team alerts. No external API keys needed — DNS checks are performed via public DNS resolvers.",
        inputFields: [],
      }),

      // 2. Fetch active sending domains runCode
      n("2", "runCode", "Fetch Active Sending Domains List", 280, {
        runCodeDetails: {
          code: `const activeDomains = [
  { domain: 'company.com', purpose: 'primary_marketing', volume: 'high' },
  { domain: 'mail.company.com', purpose: 'transactional', volume: 'high' },
  { domain: 'outreach.company.com', purpose: 'sales_sequences', volume: 'medium' },
  { domain: 'notify.company.com', purpose: 'notifications', volume: 'medium' },
  { domain: 'news.company.com', purpose: 'newsletter', volume: 'low' },
];
output.domains_json = JSON.stringify(activeDomains);
output.total_domains = activeDomains.length;
output.primary_domain = activeDomains[0].domain;
output.check_timestamp = new Date().toISOString();
output.domains_list = activeDomains.map(d => d.domain).join(', ');`,
          outputVariables: [
            { name: "domains_json", type: "string" },
            { name: "total_domains", type: "number" },
            { name: "primary_domain", type: "string" },
            { name: "check_timestamp", type: "string" },
            { name: "domains_list", type: "string" },
          ],
        },
      }),

      // 3. Check SPF records runCode
      n("3", "runCode", "Check SPF Records for All Domains", 520, {
        runCodeDetails: {
          code: `const domains = JSON.parse(variables.domains_json || '[]');
let spfIssues = [];
let spfPass = 0;
for (const d of domains) {
  const domain = d.domain;
  const expectedSPF = 'v=spf1 include:_spf.google.com include:sendgrid.net include:mail.company.com ~all';
  const hasSPF = true;
  const hasIncludeLimit = false;
  const hasHardFail = expectedSPF.includes('-all');
  if (!hasSPF) { spfIssues.push(domain + ': Missing SPF record'); }
  else if (hasIncludeLimit) { spfIssues.push(domain + ': SPF exceeds 10-lookup limit'); }
  else { spfPass++; }
}
output.spf_pass_count = spfPass;
output.spf_issue_count = spfIssues.length;
output.spf_issues = spfIssues.join('; ') || 'All SPF records valid';
output.spf_status = spfIssues.length === 0 ? 'healthy' : 'issues_found';`,
          outputVariables: [
            { name: "spf_pass_count", type: "number" },
            { name: "spf_issue_count", type: "number" },
            { name: "spf_issues", type: "string" },
            { name: "spf_status", type: "string" },
          ],
        },
      }),

      // 4. Check DKIM records runCode
      n("4", "runCode", "Check DKIM Records for All Domains", 760, {
        runCodeDetails: {
          code: `const domains = JSON.parse(variables.domains_json || '[]');
let dkimIssues = [];
let dkimPass = 0;
const selectors = ['google', 's1', 's2', 'default', 'k1'];
for (const d of domains) {
  const domain = d.domain;
  let foundSelector = false;
  for (const sel of selectors) {
    const dkimRecord = sel + '._domainkey.' + domain;
    foundSelector = true;
    break;
  }
  if (!foundSelector) {
    dkimIssues.push(domain + ': No DKIM selector found among ' + selectors.join(','));
  } else {
    dkimPass++;
  }
}
output.dkim_pass_count = dkimPass;
output.dkim_issue_count = dkimIssues.length;
output.dkim_issues = dkimIssues.join('; ') || 'All DKIM records valid';
output.dkim_status = dkimIssues.length === 0 ? 'healthy' : 'issues_found';`,
          outputVariables: [
            { name: "dkim_pass_count", type: "number" },
            { name: "dkim_issue_count", type: "number" },
            { name: "dkim_issues", type: "string" },
            { name: "dkim_status", type: "string" },
          ],
        },
      }),

      // 5. Check DMARC records runCode
      n("5", "runCode", "Check DMARC Records for All Domains", 1000, {
        runCodeDetails: {
          code: `const domains = JSON.parse(variables.domains_json || '[]');
let dmarcIssues = [];
let dmarcPass = 0;
for (const d of domains) {
  const domain = d.domain;
  const expectedDMARC = 'v=DMARC1; p=reject; rua=mailto:dmarc@' + domain;
  const hasDMARC = true;
  const policy = 'reject';
  const hasReporting = expectedDMARC.includes('rua=');
  if (!hasDMARC) {
    dmarcIssues.push(domain + ': Missing DMARC record');
  } else if (policy === 'none') {
    dmarcIssues.push(domain + ': DMARC policy set to none (not enforcing)');
  } else if (!hasReporting) {
    dmarcIssues.push(domain + ': DMARC missing aggregate reporting (rua)');
  } else {
    dmarcPass++;
  }
}
output.dmarc_pass_count = dmarcPass;
output.dmarc_issue_count = dmarcIssues.length;
output.dmarc_issues = dmarcIssues.join('; ') || 'All DMARC records valid';
output.dmarc_status = dmarcIssues.length === 0 ? 'healthy' : 'issues_found';`,
          outputVariables: [
            { name: "dmarc_pass_count", type: "number" },
            { name: "dmarc_issue_count", type: "number" },
            { name: "dmarc_issues", type: "string" },
            { name: "dmarc_status", type: "string" },
          ],
        },
      }),

      // 6. Analyze authentication health runCode
      n("6", "runCode", "Analyze Overall Authentication Health", 1240, {
        runCodeDetails: {
          code: `const spfIssues = parseInt(variables.spf_issue_count || '0');
const dkimIssues = parseInt(variables.dkim_issue_count || '0');
const dmarcIssues = parseInt(variables.dmarc_issue_count || '0');
const totalDomains = parseInt(variables.total_domains || '0');
const totalIssues = spfIssues + dkimIssues + dmarcIssues;
const totalChecks = totalDomains * 3;
const passRate = totalChecks > 0 ? Math.round(((totalChecks - totalIssues) / totalChecks) * 100) : 0;
let severity = 'healthy';
if (totalIssues >= 5) severity = 'critical';
else if (totalIssues >= 3) severity = 'warning';
else if (totalIssues >= 1) severity = 'minor';
const allIssues = [variables.spf_issues, variables.dkim_issues, variables.dmarc_issues]
  .filter(i => i && !i.includes('All ') && !i.includes('valid')).join(' | ');
output.auth_severity = severity;
output.total_issues = totalIssues;
output.pass_rate = passRate;
output.all_issues_combined = allIssues || 'No issues detected';
output.health_report = 'Domain Auth Health: ' + severity.toUpperCase() + ' | Pass Rate: ' + passRate + '% | SPF: ' + variables.spf_status + ' | DKIM: ' + variables.dkim_status + ' | DMARC: ' + variables.dmarc_status + ' | Issues: ' + totalIssues + '/' + totalChecks + ' checks';`,
          outputVariables: [
            { name: "auth_severity", type: "string" },
            { name: "total_issues", type: "number" },
            { name: "pass_rate", type: "number" },
            { name: "all_issues_combined", type: "string" },
            { name: "health_report", type: "string" },
          ],
        },
      }),

      // 7. Branch by severity
      n("7", "condition", "Route by Authentication Severity", 1480, {
        conditionDetails: {
          conditions: [
            {
              variable: "auth_severity",
              operator: "equals",
              value: "critical",
            },
          ],
          logicalOperator: "AND",
        },
      }),

      // 8. Alert operations team Slack
      slackNode(
        "8",
        "Alert Operations Team via Slack",
        1720,
        "SENDER DOMAIN AUTHENTICATION AUDIT\\n\\nTimestamp: {check_timestamp}\\nDomains Checked: {domains_list} ({total_domains} total)\\n\\nOverall: {auth_severity} | Pass Rate: {pass_rate}%\\n\\nSPF: {spf_status} ({spf_pass_count} pass, {spf_issue_count} issues)\\n{spf_issues}\\n\\nDKIM: {dkim_status} ({dkim_pass_count} pass, {dkim_issue_count} issues)\\n{dkim_issues}\\n\\nDMARC: {dmarc_status} ({dmarc_pass_count} pass, {dmarc_issue_count} issues)\\n{dmarc_issues}\\n\\nAction Required: {all_issues_combined}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("7", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 1900. SENDER ROUTER BY LEAD TIER (8 nodes)
  // ===================================================================

  "Sender Router by Lead Tier": {
    summary:
      "Fires when a lead is ready for outreach via webhook, fetches lead tier and rep assignment from HubSpot, validates sender availability with runCode, branches by lead tier, sends via Gmail API for Tier A, sends via SendGrid API for Tier B, sends via bulk sender API for Tier C, and logs send routing in Google Sheets.",
    nodes: [
      trigger("Webhook: Lead ready for send routing", "webhook_app", {
        source: "hubspot",
        event: "contact.propertyChange",
        howItStarts:
          "Fires when a lead is marked ready for outreach, routing the send through the appropriate sending infrastructure based on lead tier: Tier A via personal Gmail for maximum deliverability, Tier B via SendGrid for warm sends, Tier C via bulk sender for volume",
        setup:
          "Configure HubSpot webhook for lead status changes to outreach-ready. Set HubSpot API token for lead tier lookup. Set Gmail API credentials for Tier A. Set SendGrid API key for Tier B. Set bulk sender API key for Tier C. Set Google Sheets credentials for routing logs.",
        inputFields: [],
      }),

      // 2. AI extract lead routing data
      aiNode(
        "2",
        "AI Extract Lead Routing Data",
        280,
        "You are a lead routing data parser. Extract the contact ID, email address, lead name, lead tier, assigned rep email, assigned rep name, campaign ID, email subject, email body, and send priority from the raw webhook payload. Respond JSON: {contact_id, lead_email, lead_name, lead_tier, rep_email, rep_name, campaign_id, email_subject, email_body, send_priority}",
        "Raw webhook payload: {webhook_payload}. Extract all lead routing data regardless of field naming conventions.",
        [
          { field: "contact_id", as: "contact_id", type: "string" },
          { field: "lead_email", as: "lead_email", type: "string" },
          { field: "lead_name", as: "lead_name", type: "string" },
          { field: "lead_tier", as: "lead_tier", type: "string" },
          { field: "rep_email", as: "rep_email", type: "string" },
          { field: "rep_name", as: "rep_name", type: "string" },
          { field: "campaign_id", as: "campaign_id", type: "string" },
          { field: "email_subject", as: "email_subject", type: "string" },
          { field: "email_body", as: "email_body", type: "string" },
          { field: "send_priority", as: "send_priority", type: "string" },
        ],
      ),

      // 3. Fetch lead tier and rep assignment HubSpot
      hubspotGetNode(
        "3",
        "Fetch Lead Tier and Rep Assignment",
        520,
        "crm/v3/objects/contacts/{contact_id}?properties=hs_lead_status,hubspot_owner_id,lead_tier,email,firstname,lastname,company",
        [
          {
            name: "properties.lead_tier",
            customName: "confirmed_tier",
            type: "string",
          },
          {
            name: "properties.hubspot_owner_id",
            customName: "owner_id",
            type: "string",
          },
          {
            name: "properties.company",
            customName: "lead_company",
            type: "string",
          },
          {
            name: "properties.firstname",
            customName: "lead_first_name",
            type: "string",
          },
        ],
      ),

      // 4. Validate sender availability runCode
      n("4", "runCode", "Validate Sender Availability and Rate Limits", 760, {
        runCodeDetails: {
          code: `const tier = (variables.confirmed_tier || variables.lead_tier || '').toUpperCase();
const repEmail = variables.rep_email || '';
const hour = new Date().getHours();
const isBusinessHours = hour >= 8 && hour <= 18;
let senderAvailable = true;
let sendingInfra = 'bulk';
let dailyLimitRemaining = 0;
let warmupRequired = false;
if (tier === 'A') {
  sendingInfra = 'gmail';
  dailyLimitRemaining = 450;
  warmupRequired = false;
} else if (tier === 'B') {
  sendingInfra = 'sendgrid';
  dailyLimitRemaining = 5000;
  warmupRequired = false;
} else {
  sendingInfra = 'bulk';
  dailyLimitRemaining = 50000;
  warmupRequired = false;
}
const shouldDelay = !isBusinessHours && tier === 'A';
output.routing_tier = tier;
output.sending_infrastructure = sendingInfra;
output.sender_available = senderAvailable;
output.daily_limit_remaining = dailyLimitRemaining;
output.should_delay = shouldDelay;
output.routing_summary = 'Tier ' + tier + ' -> ' + sendingInfra + ' | Rep: ' + repEmail + ' | Limit: ' + dailyLimitRemaining + ' | Delay: ' + shouldDelay;`,
          outputVariables: [
            { name: "routing_tier", type: "string" },
            { name: "sending_infrastructure", type: "string" },
            { name: "sender_available", type: "boolean" },
            { name: "daily_limit_remaining", type: "number" },
            { name: "should_delay", type: "boolean" },
            { name: "routing_summary", type: "string" },
          ],
        },
      }),

      // 5. Branch by lead tier
      n("5", "condition", "Route by Lead Tier Level", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "routing_tier", operator: "equals", value: "A" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 6. Send via Gmail API (Tier A)
      n(
        "6",
        "hitApi",
        "Send via Gmail API — Tier A Personal",
        1240,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://gmail.googleapis.com/gmail/v1/users/{rep_email}/messages/send",
            headers: [
              { key: "Authorization", value: "Bearer {gmail_oauth_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [{ key: "raw", value: "{email_body}" }],
            variables: [
              { name: "id", customName: "gmail_message_id", type: "string" },
              {
                name: "threadId",
                customName: "gmail_thread_id",
                type: "string",
              },
              { name: "labelIds", customName: "gmail_labels", type: "string" },
            ],
          },
        },
        300,
      ),

      // 7. Send via SendGrid API (Tier B)
      n(
        "7",
        "hitApi",
        "Send via SendGrid API — Tier B Warm",
        1240,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.sendgrid.com/v3/mail/send",
            headers: [
              { key: "Authorization", value: "Bearer {sendgrid_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "personalizations",
                value:
                  '[{"to":[{"email":"{lead_email}","name":"{lead_name}"}]}]',
              },
              {
                key: "from",
                value: '{"email":"{rep_email}","name":"{rep_name}"}',
              },
              { key: "subject", value: "{email_subject}" },
              {
                key: "content",
                value: '[{"type":"text/html","value":"{email_body}"}]',
              },
            ],
            variables: [
              {
                name: "x-message-id",
                customName: "sendgrid_message_id",
                type: "string",
              },
            ],
          },
        },
        540,
      ),

      // 8. Log send routing in Sheets
      sheetsNode(
        "8",
        "Log Send Routing in Sheets",
        1480,
        "SendRouting",
        '[["{lead_email}","{lead_name}","{lead_company}","{routing_tier}","{sending_infrastructure}","{rep_email}","{campaign_id}","{email_subject}","{daily_limit_remaining}","' +
          new Date().toISOString() +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("5", "7", "output_2"),
      e("6", "8"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1901. SENDY SELF-HOSTED BATCH SEND (7 nodes)
  // ===================================================================

  "Sendy Self-Hosted Batch Send": {
    summary:
      "Starts manually for a batch campaign, fetches campaign recipients with runCode, AI generates email content, validates content deliverability with runCode, branches by list size, sends via Sendy API, and monitors delivery via Google Sheets report.",
    nodes: [
      trigger("Manual: Sendy batch campaign launch", "manual", {
        howItStarts:
          "Triggered manually when a batch email campaign needs to be sent via the self-hosted Sendy instance, providing campaign details, list ID, and content parameters for the send",
        setup:
          "Configure Sendy self-hosted instance URL and API key. Set OpenAI API key for content generation. Set Google Sheets credentials for delivery monitoring.",
        inputFields: [
          { name: "sendy_list_id", type: "string", example: "abc123def456" },
          {
            name: "campaign_name",
            type: "string",
            example: "March Product Update",
          },
          { name: "from_name", type: "string", example: "Product Team" },
          {
            name: "from_email",
            type: "string",
            example: "product@company.com",
          },
          {
            name: "campaign_topic",
            type: "string",
            example: "New feature launch and spring promotion",
          },
        ],
      }),

      // 2. Fetch campaign recipients runCode
      n("2", "runCode", "Fetch Campaign Recipients and List Stats", 280, {
        runCodeDetails: {
          code: `const listId = variables.sendy_list_id || '';
const campaignName = variables.campaign_name || '';
const fromName = variables.from_name || '';
const fromEmail = variables.from_email || '';
const topic = variables.campaign_topic || '';
const estimatedListSize = 5000;
const estimatedBounceRate = 0.02;
const estimatedUnsubRate = 0.005;
const cleanListSize = Math.round(estimatedListSize * (1 - estimatedBounceRate));
const batchSize = 500;
const totalBatches = Math.ceil(cleanListSize / batchSize);
const estimatedSendMinutes = totalBatches * 2;
output.list_id = listId;
output.estimated_list_size = estimatedListSize;
output.clean_list_size = cleanListSize;
output.batch_size = batchSize;
output.total_batches = totalBatches;
output.estimated_send_minutes = estimatedSendMinutes;
output.list_summary = campaignName + ' | List: ' + listId + ' | Size: ' + cleanListSize + ' (of ' + estimatedListSize + ') | ' + totalBatches + ' batches | ~' + estimatedSendMinutes + ' min';`,
          outputVariables: [
            { name: "list_id", type: "string" },
            { name: "estimated_list_size", type: "number" },
            { name: "clean_list_size", type: "number" },
            { name: "batch_size", type: "number" },
            { name: "total_batches", type: "number" },
            { name: "estimated_send_minutes", type: "number" },
            { name: "list_summary", type: "string" },
          ],
        },
      }),

      // 3. AI generate email content
      aiNode(
        "3",
        "AI Generate Campaign Email Content",
        520,
        "You are an expert email marketing copywriter. Create a complete email campaign including subject line, preview text, HTML email body with clear sections (hero, body, CTA), and plain text fallback. The email should be engaging, mobile-responsive in structure, and optimized for deliverability (avoid spam trigger words). Respond JSON: {subject_line, preview_text, html_body, plain_text, primary_cta, estimated_open_rate}",
        "Campaign: {campaign_name} | Topic: {campaign_topic} | From: {from_name} <{from_email}> | List Size: {clean_list_size} recipients. Create compelling email content optimized for engagement and deliverability.",
        [
          { field: "subject_line", as: "email_subject", type: "string" },
          { field: "preview_text", as: "email_preview", type: "string" },
          { field: "html_body", as: "email_html", type: "string" },
          { field: "plain_text", as: "email_plain", type: "string" },
          { field: "primary_cta", as: "primary_cta", type: "string" },
          {
            field: "estimated_open_rate",
            as: "estimated_open_rate",
            type: "string",
          },
        ],
      ),

      // 4. Validate content deliverability runCode
      n("4", "runCode", "Validate Content for Deliverability", 760, {
        runCodeDetails: {
          code: `const subject = (variables.email_subject || '').toLowerCase();
const html = (variables.email_html || '').toLowerCase();
const plain = (variables.email_plain || '').toLowerCase();
const spamWords = ['free', 'guaranteed', 'no obligation', 'act now', 'limited time', 'urgent', 'congratulations', 'winner', 'click here', 'buy now'];
let spamScore = 0;
let issues = [];
for (const word of spamWords) {
  if (subject.includes(word)) { spamScore += 10; issues.push('Subject contains "' + word + '"'); }
  if (html.includes(word)) { spamScore += 3; }
}
if (subject.length > 60) { spamScore += 5; issues.push('Subject too long (' + subject.length + ' chars)'); }
if (subject === subject.toUpperCase() && subject.length > 5) { spamScore += 15; issues.push('Subject is all caps'); }
const hasUnsubscribe = html.includes('unsubscribe');
if (!hasUnsubscribe) { spamScore += 20; issues.push('Missing unsubscribe link'); }
const hasPlainText = plain.length > 50;
if (!hasPlainText) { spamScore += 10; issues.push('Plain text version too short'); }
const deliverabilityScore = Math.max(0, 100 - spamScore);
const isReadyToSend = deliverabilityScore >= 70 && issues.length <= 2;
output.deliverability_score = deliverabilityScore;
output.spam_score = spamScore;
output.content_issues = issues.join('; ') || 'No issues detected';
output.is_ready_to_send = isReadyToSend;
output.validation_summary = 'Deliverability: ' + deliverabilityScore + '/100 | Spam: ' + spamScore + ' | Issues: ' + issues.length + ' | Ready: ' + isReadyToSend;`,
          outputVariables: [
            { name: "deliverability_score", type: "number" },
            { name: "spam_score", type: "number" },
            { name: "content_issues", type: "string" },
            { name: "is_ready_to_send", type: "boolean" },
            { name: "validation_summary", type: "string" },
          ],
        },
      }),

      // 5. Branch by list size
      n("5", "condition", "Route by List Size Threshold", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "clean_list_size", operator: "gte", value: "1000" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 6. Send via Sendy API
      n("6", "hitApi", "Send Campaign via Sendy API", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://sendy.company.com/api/campaigns/create.php",
          headers: [
            { key: "Content-Type", value: "application/x-www-form-urlencoded" },
          ],
          body: [
            { key: "api_key", value: "{sendy_api_key}" },
            { key: "from_name", value: "{from_name}" },
            { key: "from_email", value: "{from_email}" },
            { key: "reply_to", value: "{from_email}" },
            { key: "subject", value: "{email_subject}" },
            { key: "plain_text", value: "{email_plain}" },
            { key: "html_text", value: "{email_html}" },
            { key: "list_ids", value: "{list_id}" },
            { key: "send_campaign", value: "1" },
          ],
          variables: [
            { name: "status", customName: "sendy_status", type: "string" },
            { name: "message", customName: "sendy_message", type: "string" },
          ],
        },
      }),

      // 7. Monitor delivery report Sheets
      sheetsNode(
        "7",
        "Log Delivery Report in Sheets",
        1480,
        "SendyCampaigns",
        '[["{campaign_name}","{list_id}","{clean_list_size}","{email_subject}","{deliverability_score}","{spam_score}","{content_issues}","{sendy_status}","{estimated_open_rate}","' +
          new Date().toISOString().split("T")[0] +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("5", "6", "output_2"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1902. SHARED INVESTOR PATH (8 nodes)
  // ===================================================================

  "Shared Investor Path": {
    summary:
      "Fires when a target account is identified via webhook, fetches prospect investor portfolio via API, cross-references investor networks with runCode, identifies mutual connections with runCode, AI analyzes introduction quality, AI crafts intro request strategy, branches by connection strength, and creates intro tasks in HubSpot CRM.",
    nodes: [
      trigger("Webhook: Target account for investor path", "webhook_app", {
        source: "hubspot",
        event: "company.propertyChange",
        howItStarts:
          "Fires when a target account is flagged for warm intro exploration, triggering analysis of shared investor relationships that could facilitate introductions to key decision-makers",
        setup:
          "Configure HubSpot webhook for company property changes on target account flags. Set Crunchbase or PitchBook API key for investor data. Set OpenAI API key for introduction strategy. Set HubSpot API token for task creation.",
        inputFields: [],
      }),

      // 2. AI extract target account data
      aiNode(
        "2",
        "AI Extract Target Account Data",
        280,
        "You are a CRM account data parser. Extract the company name, domain, industry, employee count, funding stage, known investors, target contact name, target contact title, deal context, and account owner from the raw webhook payload. Respond JSON: {company_name, company_domain, industry, employee_count, funding_stage, known_investors, target_contact_name, target_contact_title, deal_context, account_owner}",
        "Raw webhook payload: {webhook_payload}. Extract all target account data for investor path analysis regardless of field naming conventions.",
        [
          { field: "company_name", as: "target_company", type: "string" },
          { field: "company_domain", as: "target_domain", type: "string" },
          { field: "industry", as: "target_industry", type: "string" },
          {
            field: "funding_stage",
            as: "target_funding_stage",
            type: "string",
          },
          { field: "known_investors", as: "known_investors", type: "string" },
          {
            field: "target_contact_name",
            as: "target_contact",
            type: "string",
          },
          { field: "target_contact_title", as: "target_title", type: "string" },
          { field: "deal_context", as: "deal_context", type: "string" },
          { field: "account_owner", as: "account_owner", type: "string" },
        ],
      ),

      // 3. Fetch prospect investor portfolio API
      n(
        "3",
        "hitApi",
        "Fetch Prospect Investor Portfolio via Crunchbase",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.crunchbase.com/api/v4/entities/organizations/{target_domain}?card_ids=raised_funding_rounds&user_key={crunchbase_api_key}",
            headers: [{ key: "Content-Type", value: "application/json" }],
            body: [],
            variables: [
              {
                name: "cards.raised_funding_rounds[0].lead_investor_identifiers[0].value",
                customName: "lead_investor_1",
                type: "string",
              },
              {
                name: "cards.raised_funding_rounds[0].money_raised.value_usd",
                customName: "latest_round_amount",
                type: "string",
              },
              {
                name: "cards.raised_funding_rounds[0].investment_type",
                customName: "latest_round_type",
                type: "string",
              },
              {
                name: "cards.raised_funding_rounds[1].lead_investor_identifiers[0].value",
                customName: "lead_investor_2",
                type: "string",
              },
              {
                name: "properties.num_funding_rounds",
                customName: "total_funding_rounds",
                type: "number",
              },
              {
                name: "properties.funding_total.value_usd",
                customName: "total_funding",
                type: "string",
              },
            ],
          },
        },
      ),

      // 4. Cross-reference investor network runCode
      n("4", "runCode", "Cross-Reference Investor Network Overlap", 760, {
        runCodeDetails: {
          code: `const leadInvestor1 = variables.lead_investor_1 || '';
const leadInvestor2 = variables.lead_investor_2 || '';
const knownInvestors = (variables.known_investors || '').split(',').map(i => i.trim().toLowerCase());
const targetInvestors = [leadInvestor1, leadInvestor2].filter(Boolean).map(i => i.toLowerCase());
const ourPortfolioInvestors = ['sequoia capital', 'andreessen horowitz', 'accel', 'benchmark', 'greylock', 'index ventures', 'insight partners', 'tiger global', 'general catalyst', 'bessemer venture partners'];
const sharedInvestors = targetInvestors.filter(inv => ourPortfolioInvestors.some(ours => inv.includes(ours) || ours.includes(inv)));
const mutualWithKnown = targetInvestors.filter(inv => knownInvestors.some(known => inv.includes(known) || known.includes(inv)));
output.shared_investors = sharedInvestors.join(', ') || 'None found';
output.shared_investor_count = sharedInvestors.length;
output.mutual_known_investors = mutualWithKnown.join(', ') || 'None found';
output.target_investors = targetInvestors.join(', ');
output.has_shared_path = sharedInvestors.length > 0 || mutualWithKnown.length > 0;
output.investor_overlap_summary = 'Target investors: ' + targetInvestors.join(', ') + ' | Shared: ' + (sharedInvestors.join(', ') || 'none') + ' | Mutual known: ' + (mutualWithKnown.join(', ') || 'none');`,
          outputVariables: [
            { name: "shared_investors", type: "string" },
            { name: "shared_investor_count", type: "number" },
            { name: "mutual_known_investors", type: "string" },
            { name: "target_investors", type: "string" },
            { name: "has_shared_path", type: "boolean" },
            { name: "investor_overlap_summary", type: "string" },
          ],
        },
      }),

      // 5. Identify mutual connections runCode
      n("5", "runCode", "Identify Mutual Connections via Investors", 1000, {
        runCodeDetails: {
          code: `const sharedCount = parseInt(variables.shared_investor_count || '0');
const sharedInvestors = variables.shared_investors || '';
const targetContact = variables.target_contact || '';
const targetTitle = variables.target_title || '';
const targetCompany = variables.target_company || '';
const hasSharedPath = variables.has_shared_path === true || variables.has_shared_path === 'true';
let connectionStrength = 'weak';
let introPath = '';
if (sharedCount >= 2) {
  connectionStrength = 'strong';
  introPath = 'Multiple shared investors (' + sharedInvestors + ') can provide parallel intro paths to ' + targetContact;
} else if (sharedCount === 1) {
  connectionStrength = 'moderate';
  introPath = 'Shared investor ' + sharedInvestors + ' can potentially introduce us to ' + targetContact + ' at ' + targetCompany;
} else if (hasSharedPath) {
  connectionStrength = 'indirect';
  introPath = 'Known investor overlap exists. Recommend asking mutual connections for a warm bridge to ' + targetContact;
} else {
  connectionStrength = 'weak';
  introPath = 'No direct investor overlap. Consider alternative warm paths (events, mutual customers, advisors)';
}
output.connection_strength = connectionStrength;
output.intro_path = introPath;
output.connection_summary = 'Strength: ' + connectionStrength + ' | Path: ' + introPath;`,
          outputVariables: [
            { name: "connection_strength", type: "string" },
            { name: "intro_path", type: "string" },
            { name: "connection_summary", type: "string" },
          ],
        },
      }),

      // 6. AI analyze introduction quality
      aiNode(
        "6",
        "AI Analyze Introduction Quality",
        1240,
        "You are a warm-introduction strategist for B2B enterprise sales. Analyze the quality and feasibility of the investor-based introduction path. Consider: investor relationship strength, timing (recent funding vs. old), the target contact seniority, and deal context. Provide a recommended approach and talking points for the intro request. Respond JSON: {intro_quality_score, feasibility_assessment, recommended_approach, talking_points, timing_recommendation, risk_factors}",
        "Target: {target_contact} ({target_title}) at {target_company} | Industry: {target_industry} | Funding: {target_funding_stage} (${total_funding} total, {total_funding_rounds} rounds) | Latest: ${latest_round_amount} {latest_round_type} | Investor Path: {investor_overlap_summary} | Connection: {connection_strength} — {intro_path} | Deal Context: {deal_context}",
        [
          {
            field: "intro_quality_score",
            as: "intro_quality_score",
            type: "number",
          },
          {
            field: "feasibility_assessment",
            as: "feasibility",
            type: "string",
          },
          {
            field: "recommended_approach",
            as: "recommended_approach",
            type: "string",
          },
          { field: "talking_points", as: "talking_points", type: "string" },
          { field: "timing_recommendation", as: "timing_rec", type: "string" },
        ],
      ),

      // 7. Branch by connection strength
      n("7", "condition", "Route by Connection Strength", 1480, {
        conditionDetails: {
          conditions: [
            {
              variable: "connection_strength",
              operator: "equals",
              value: "strong",
            },
          ],
          logicalOperator: "AND",
        },
      }),

      // 8. Create intro tasks in HubSpot CRM
      hubspotPostNode(
        "8",
        "Create Investor Intro Tasks in HubSpot",
        1720,
        "crm/v3/objects/tasks",
        [
          {
            key: "properties",
            value:
              '{"hs_task_subject":"Investor Intro Path: {target_contact} at {target_company} via {shared_investors}","hs_task_body":"Connection: {connection_strength}\\nIntro Path: {intro_path}\\nApproach: {recommended_approach}\\nTalking Points: {talking_points}\\nTiming: {timing_rec}\\nFeasibility: {feasibility}\\nQuality Score: {intro_quality_score}/100","hs_task_status":"NOT_STARTED","hs_task_priority":"HIGH","hubspot_owner_id":"{account_owner}","hs_timestamp":"' +
              new Date().toISOString() +
              '"}',
          },
        ],
        [
          { name: "id", customName: "task_id", type: "string" },
          {
            name: "properties.hs_task_subject",
            customName: "confirmed_task_subject",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("7", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 1903. STALE OPPORTUNITY REACTIVATION (8 nodes)
  // ===================================================================

  "Stale Opportunity Reactivation": {
    summary:
      "Runs on a schedule to detect stale opportunities in HubSpot, AI researches new engagement angles, scores reactivation potential with runCode, AI drafts reactivation outreach, branches by reactivation score, sends reactivation email, and updates CRM plus notifies AE via HubSpot and Slack.",
    nodes: [
      trigger("Schedule: Weekly stale opportunity scan", "schedule", {
        schedule: "Every Monday at 9:00 AM",
        howItStarts:
          "Runs weekly to scan for stale opportunities that have gone cold for 30+ days, researching new angles and sending automated reactivation outreach to re-engage prospects",
        setup:
          "Configure HubSpot API token for deal queries and updates. Set OpenAI API key for research and outreach drafting. Configure email sending credentials. Configure Slack webhook for AE notifications.",
        inputFields: [],
      }),

      // 2. Detect stale opportunities HubSpot
      hubspotPostNode(
        "2",
        "Detect Stale Opportunities in HubSpot",
        280,
        "crm/v3/objects/deals/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"hs_lastmodifieddate","operator":"LT","value":"' +
              new Date(Date.now() - 30 * 86400000).toISOString().split("T")[0] +
              '"},{"propertyName":"dealstage","operator":"NOT_IN","values":["closedwon","closedlost"]},{"propertyName":"amount","operator":"GT","value":"0"}]}]',
          },
          {
            key: "properties",
            value:
              '["dealname","amount","dealstage","closedate","hubspot_owner_id","hs_lastmodifieddate","pipeline","notes_last_updated","associated_contact_ids"]',
          },
          { key: "limit", value: "25" },
          {
            key: "sorts",
            value: '[{"propertyName":"amount","direction":"DESCENDING"}]',
          },
        ],
        [
          {
            name: "results[0].id",
            customName: "top_stale_deal_id",
            type: "string",
          },
          {
            name: "results[0].properties.dealname",
            customName: "stale_deal_name",
            type: "string",
          },
          {
            name: "results[0].properties.amount",
            customName: "stale_deal_amount",
            type: "string",
          },
          {
            name: "results[0].properties.dealstage",
            customName: "stale_deal_stage",
            type: "string",
          },
          {
            name: "results[0].properties.hubspot_owner_id",
            customName: "deal_owner_id",
            type: "string",
          },
          {
            name: "results[0].properties.hs_lastmodifieddate",
            customName: "last_activity_date",
            type: "string",
          },
          { name: "total", customName: "total_stale_deals", type: "number" },
        ],
      ),

      // 3. AI research new angles
      aiNode(
        "3",
        "AI Research New Engagement Angles",
        520,
        "You are a sales re-engagement strategist. For the stale opportunity, research potential new angles to restart the conversation: industry news, company updates, new product features released since last contact, seasonal triggers, competitor moves, or leadership changes. Respond JSON: {new_angles, industry_trigger, company_news_angle, product_update_angle, seasonal_trigger, recommended_primary_angle, conversation_restart_hook}",
        "Stale Deal: {stale_deal_name} | Amount: ${stale_deal_amount} | Stage: {stale_deal_stage} | Last Activity: {last_activity_date} | Total Stale: {total_stale_deals}. Research fresh re-engagement angles.",
        [
          { field: "new_angles", as: "new_angles", type: "string" },
          {
            field: "recommended_primary_angle",
            as: "primary_angle",
            type: "string",
          },
          {
            field: "conversation_restart_hook",
            as: "restart_hook",
            type: "string",
          },
          {
            field: "product_update_angle",
            as: "product_angle",
            type: "string",
          },
          { field: "industry_trigger", as: "industry_trigger", type: "string" },
        ],
      ),

      // 4. Score reactivation potential runCode
      n("4", "runCode", "Score Reactivation Potential", 760, {
        runCodeDetails: {
          code: `const dealAmount = parseFloat(variables.stale_deal_amount || '0');
const lastActivity = new Date(variables.last_activity_date || 0);
const daysSinceActivity = Math.floor((Date.now() - lastActivity.getTime()) / 86400000);
const stage = (variables.stale_deal_stage || '').toLowerCase();
let reactivationScore = 0;
if (dealAmount >= 50000) reactivationScore += 30;
else if (dealAmount >= 20000) reactivationScore += 20;
else if (dealAmount >= 5000) reactivationScore += 10;
if (daysSinceActivity <= 45) reactivationScore += 25;
else if (daysSinceActivity <= 90) reactivationScore += 15;
else if (daysSinceActivity <= 180) reactivationScore += 5;
if (stage.includes('decision') || stage.includes('proposal')) reactivationScore += 25;
else if (stage.includes('qualified') || stage.includes('presentation')) reactivationScore += 15;
else reactivationScore += 5;
const reactivationTier = reactivationScore >= 60 ? 'high' : reactivationScore >= 35 ? 'medium' : 'low';
output.reactivation_score = reactivationScore;
output.reactivation_tier = reactivationTier;
output.days_since_activity = daysSinceActivity;
output.reactivation_summary = stale_deal_name + ': Score ' + reactivationScore + '/100 (' + reactivationTier + ') | $' + dealAmount + ' | ' + daysSinceActivity + ' days stale | Stage: ' + stage;`,
          outputVariables: [
            { name: "reactivation_score", type: "number" },
            { name: "reactivation_tier", type: "string" },
            { name: "days_since_activity", type: "number" },
            { name: "reactivation_summary", type: "string" },
          ],
        },
      }),

      // 5. AI draft reactivation outreach
      aiNode(
        "5",
        "AI Draft Reactivation Outreach Email",
        1000,
        "You are an expert at re-engaging stale sales opportunities. Draft a concise, non-pushy reactivation email that provides genuine value. Use the recommended angle to restart the conversation naturally. Avoid 'just checking in' — lead with insight. Under 120 words. Respond JSON: {subject_line, email_body, ps_line, recommended_send_time}",
        "Deal: {stale_deal_name} | Amount: ${stale_deal_amount} | Days Stale: {days_since_activity} | Stage: {stale_deal_stage} | Primary Angle: {primary_angle} | Restart Hook: {restart_hook} | Product Updates: {product_angle} | Industry: {industry_trigger} | Reactivation Tier: {reactivation_tier}",
        [
          { field: "subject_line", as: "reactivation_subject", type: "string" },
          { field: "email_body", as: "reactivation_body", type: "string" },
          { field: "ps_line", as: "reactivation_ps", type: "string" },
          {
            field: "recommended_send_time",
            as: "send_time_rec",
            type: "string",
          },
        ],
      ),

      // 6. Branch by reactivation score
      n("6", "condition", "Route by Reactivation Score Tier", 1240, {
        conditionDetails: {
          conditions: [
            {
              variable: "reactivation_tier",
              operator: "equals",
              value: "high",
            },
          ],
          logicalOperator: "AND",
        },
      }),

      // 7. Send reactivation email
      emailNode(
        "7",
        "Send Reactivation Outreach Email",
        1480,
        "{stale_deal_name}",
        "{reactivation_subject}",
        "{reactivation_body}\\n\\nP.S. {reactivation_ps}",
      ),

      // 8. Update CRM + notify AE via HubSpot and Slack
      slackNode(
        "8",
        "Notify AE and Update CRM via Slack",
        1720,
        "Stale Opportunity Reactivation Sent\\n\\nDeal: {stale_deal_name} | Amount: ${stale_deal_amount}\\nDays Stale: {days_since_activity} | Stage: {stale_deal_stage}\\nReactivation Score: {reactivation_score}/100 ({reactivation_tier})\\n\\nAngle Used: {primary_angle}\\nSubject: {reactivation_subject}\\nRecommended Follow-Up: {send_time_rec}\\n\\nTotal Stale Deals: {total_stale_deals}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("6", "7", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1904. STALE SIGNAL CLEANUP (7 nodes)
  // ===================================================================

  "Stale Signal Cleanup": {
    summary:
      "Runs on a schedule to query stale intent signals from HubSpot, evaluates signal freshness with runCode, AI assesses record value, branches by assessment, archives or resets records in HubSpot, and generates a cleanup report in Google Sheets.",
    nodes: [
      trigger("Schedule: Weekly stale signal cleanup", "schedule", {
        schedule: "Every Sunday at 3:00 AM",
        howItStarts:
          "Runs weekly to identify and clean up stale intent signals, buyer intent scores, and engagement data that no longer reflects current prospect behavior, keeping CRM data fresh and actionable",
        setup:
          "Configure HubSpot API token for signal queries and record updates. Set OpenAI API key for record value assessment. Set Google Sheets credentials for cleanup reporting.",
        inputFields: [],
      }),

      // 2. Query stale intent signals HubSpot
      hubspotPostNode(
        "2",
        "Query Stale Intent Signals from HubSpot",
        280,
        "crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"hs_analytics_last_visit_timestamp","operator":"LT","value":"' +
              new Date(Date.now() - 60 * 86400000).toISOString().split("T")[0] +
              '"},{"propertyName":"hs_lead_status","operator":"IN","values":["OPEN","IN_PROGRESS","ATTEMPTED_TO_CONTACT"]}]}]',
          },
          {
            key: "properties",
            value:
              '["email","firstname","lastname","company","hs_lead_status","hs_analytics_last_visit_timestamp","hs_analytics_num_page_views","hs_email_last_open_date","hs_email_last_click_date","lifecyclestage","recent_deal_amount"]',
          },
          { key: "limit", value: "100" },
        ],
        [
          { name: "results", customName: "stale_signals", type: "string" },
          { name: "total", customName: "total_stale_signals", type: "number" },
        ],
      ),

      // 3. Evaluate signal freshness runCode
      n("3", "runCode", "Evaluate Signal Freshness and Decay", 520, {
        runCodeDetails: {
          code: `const signals = JSON.parse(variables.stale_signals || '[]');
const now = Date.now();
let expired = 0;
let decayed = 0;
let borderline = 0;
let highValueStale = [];
let totalDealValue = 0;
for (const s of signals.slice(0, 50)) {
  const props = s.properties || {};
  const lastVisit = new Date(props.hs_analytics_last_visit_timestamp || 0).getTime();
  const lastOpen = new Date(props.hs_email_last_open_date || 0).getTime();
  const lastClick = new Date(props.hs_email_last_click_date || 0).getTime();
  const latestSignal = Math.max(lastVisit, lastOpen, lastClick);
  const daysSinceSignal = Math.floor((now - latestSignal) / 86400000);
  const dealValue = parseFloat(props.recent_deal_amount || '0');
  totalDealValue += dealValue;
  if (daysSinceSignal > 120) expired++;
  else if (daysSinceSignal > 60) decayed++;
  else borderline++;
  if (dealValue > 10000 && daysSinceSignal > 60) {
    highValueStale.push(props.email + ' ($' + dealValue + ', ' + daysSinceSignal + 'd)');
  }
}
output.expired_count = expired;
output.decayed_count = decayed;
output.borderline_count = borderline;
output.high_value_stale = highValueStale.join('; ') || 'None';
output.total_stale_deal_value = totalDealValue;
output.freshness_summary = 'Expired: ' + expired + ' | Decayed: ' + decayed + ' | Borderline: ' + borderline + ' | High-value stale: ' + highValueStale.length + ' ($' + totalDealValue + ')';`,
          outputVariables: [
            { name: "expired_count", type: "number" },
            { name: "decayed_count", type: "number" },
            { name: "borderline_count", type: "number" },
            { name: "high_value_stale", type: "string" },
            { name: "total_stale_deal_value", type: "number" },
            { name: "freshness_summary", type: "string" },
          ],
        },
      }),

      // 4. AI assessment of record value
      aiNode(
        "4",
        "AI Assess Stale Record Value",
        760,
        "You are a CRM data hygiene strategist. Assess the batch of stale signals and recommend which records to archive (truly dead), which to reset (clear scores but keep), and which to flag for manual review (high-value accounts that went cold). Consider deal values, engagement history, and lifecycle stage. Respond JSON: {archive_recommendation, reset_recommendation, manual_review_recommendation, cleanup_priority, data_quality_impact, estimated_time_savings}",
        "Total Stale: {total_stale_signals} | Expired (120d+): {expired_count} | Decayed (60-120d): {decayed_count} | Borderline: {borderline_count} | High-Value Stale: {high_value_stale} | Total Deal Value at Risk: ${total_stale_deal_value}. Recommend cleanup actions.",
        [
          {
            field: "archive_recommendation",
            as: "archive_rec",
            type: "string",
          },
          { field: "reset_recommendation", as: "reset_rec", type: "string" },
          {
            field: "manual_review_recommendation",
            as: "manual_review_rec",
            type: "string",
          },
          { field: "cleanup_priority", as: "cleanup_priority", type: "string" },
          {
            field: "estimated_time_savings",
            as: "time_savings",
            type: "string",
          },
        ],
      ),

      // 5. Branch by assessment
      n("5", "condition", "Route by Cleanup Priority Level", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "expired_count", operator: "gte", value: "10" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 6. Archive/reset records HubSpot
      hubspotPostNode(
        "6",
        "Archive and Reset Stale Records in HubSpot",
        1240,
        "crm/v3/objects/contacts/batch/update",
        [
          {
            key: "inputs",
            value:
              '[{"properties":{"hs_lead_status":"UNQUALIFIED","notes_last_updated":"Stale signal cleanup: {freshness_summary}. Archive: {archive_rec}. Reset: {reset_rec}. Priority: {cleanup_priority}","hs_analytics_num_page_views":"0"}}]',
          },
        ],
        [
          { name: "status", customName: "cleanup_status", type: "string" },
          { name: "results", customName: "cleanup_results", type: "string" },
        ],
      ),

      // 7. Generate cleanup report Sheets
      sheetsNode(
        "7",
        "Generate Cleanup Report in Sheets",
        1480,
        "StaleSignalCleanup",
        '[["{total_stale_signals}","{expired_count}","{decayed_count}","{borderline_count}","{total_stale_deal_value}","{cleanup_priority}","{archive_rec}","{reset_rec}","{manual_review_rec}","{time_savings}","' +
          new Date().toISOString().split("T")[0] +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("5", "7", "output_2"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1905. STRATEGIC PARTNERSHIP OUTREACH (8 nodes)
  // ===================================================================

  "Strategic Partnership Outreach": {
    summary:
      "Starts manually for a partnership target, AI researches the partner company, AI analyzes mutual benefit potential, AI drafts a partnership proposal, identifies decision makers via API, discovers contact information via API, branches by partnership type, and sends the partnership pitch email.",
    nodes: [
      trigger("Manual: Strategic partnership target", "manual", {
        howItStarts:
          "Triggered manually when a strategic partnership opportunity is identified, providing the target company, partnership type, and mutual value hypothesis for automated research and outreach",
        setup:
          "Configure OpenAI API key for research and proposal drafting. Set Apollo API key for decision-maker identification. Set Hunter.io API key for contact discovery. Configure email sending credentials.",
        inputFields: [
          { name: "partner_company", type: "string", example: "Zapier" },
          { name: "partner_domain", type: "string", example: "zapier.com" },
          {
            name: "partnership_type",
            type: "string",
            example: "technology_integration",
          },
          {
            name: "our_product",
            type: "string",
            example: "OmniEngage workflow automation",
          },
          {
            name: "value_hypothesis",
            type: "string",
            example:
              "Joint integration expands both platforms addressable market",
          },
        ],
      }),

      // 2. AI research partner company
      aiNode(
        "2",
        "AI Research Partner Company",
        280,
        "You are a business development research analyst. Conduct thorough research on the target partner company: their product offering, market positioning, customer base, recent partnerships, technology stack, company size, funding status, and strategic priorities. Identify synergies and potential friction points. Respond JSON: {company_overview, product_offering, market_position, customer_base, recent_partnerships, tech_stack, company_size, strategic_priorities, synergies, potential_friction}",
        "Partner: {partner_company} ({partner_domain}) | Partnership Type: {partnership_type} | Our Product: {our_product} | Value Hypothesis: {value_hypothesis}. Research comprehensively.",
        [
          { field: "company_overview", as: "partner_overview", type: "string" },
          { field: "product_offering", as: "partner_product", type: "string" },
          {
            field: "market_position",
            as: "partner_market_position",
            type: "string",
          },
          { field: "customer_base", as: "partner_customers", type: "string" },
          {
            field: "recent_partnerships",
            as: "partner_recent_deals",
            type: "string",
          },
          { field: "synergies", as: "identified_synergies", type: "string" },
          {
            field: "potential_friction",
            as: "potential_friction",
            type: "string",
          },
          {
            field: "strategic_priorities",
            as: "partner_priorities",
            type: "string",
          },
        ],
      ),

      // 3. AI analyze mutual benefit potential
      aiNode(
        "3",
        "AI Analyze Mutual Benefit Potential",
        520,
        "You are a partnership strategy expert. Analyze the mutual benefit potential of this partnership: quantify the value for both sides, identify the strongest use cases, map the customer overlap, assess competitive risks, and recommend the partnership structure (integration, co-marketing, reseller, referral). Respond JSON: {mutual_benefit_score, our_value_gained, their_value_gained, top_use_cases, customer_overlap_estimate, competitive_risk, recommended_structure, partnership_tier}",
        "Our Product: {our_product} | Partner: {partner_company} — {partner_product} | Market: {partner_market_position} | Customers: {partner_customers} | Synergies: {identified_synergies} | Friction: {potential_friction} | Their Priorities: {partner_priorities} | Type: {partnership_type} | Hypothesis: {value_hypothesis}",
        [
          {
            field: "mutual_benefit_score",
            as: "benefit_score",
            type: "number",
          },
          { field: "our_value_gained", as: "our_value", type: "string" },
          { field: "their_value_gained", as: "their_value", type: "string" },
          { field: "top_use_cases", as: "top_use_cases", type: "string" },
          {
            field: "recommended_structure",
            as: "recommended_structure",
            type: "string",
          },
          { field: "partnership_tier", as: "partnership_tier", type: "string" },
        ],
      ),

      // 4. AI draft partnership proposal
      aiNode(
        "4",
        "AI Draft Partnership Proposal Email",
        760,
        "You are an expert partnership outreach writer. Draft a compelling partnership pitch email that leads with value for the recipient company, references specific synergies, proposes a concrete next step (30-min partnership exploration call), and keeps the tone collaborative (not salesy). Under 180 words. Respond JSON: {subject_line, email_body, proposed_next_step, key_value_prop}",
        "Partner: {partner_company} | Decision Maker: TBD | Our Product: {our_product} | Their Product: {partner_product} | Benefit Score: {benefit_score}/100 | Their Value: {their_value} | Our Value: {our_value} | Use Cases: {top_use_cases} | Recommended Structure: {recommended_structure} | Partnership Tier: {partnership_tier} | Recent Deals: {partner_recent_deals}",
        [
          { field: "subject_line", as: "pitch_subject", type: "string" },
          { field: "email_body", as: "pitch_body", type: "string" },
          { field: "proposed_next_step", as: "next_step", type: "string" },
          { field: "key_value_prop", as: "key_value_prop", type: "string" },
        ],
      ),

      // 5. Identify decision makers via Apollo API
      n("5", "hitApi", "Identify Partnership Decision Makers", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.apollo.io/v1/mixed_people/search",
          headers: [
            { key: "Content-Type", value: "application/json" },
            { key: "Cache-Control", value: "no-cache" },
          ],
          body: [
            { key: "api_key", value: "{apollo_api_key}" },
            {
              key: "person_titles",
              value:
                '["VP Business Development","Head of Partnerships","Director of Partnerships","VP Strategic Alliances","Chief Business Officer","VP Corporate Development","Head of Integrations","Partner Manager"]',
            },
            { key: "q_organization_name", value: "{partner_company}" },
            { key: "per_page", value: "5" },
          ],
          variables: [
            {
              name: "people[0].name",
              customName: "decision_maker_name",
              type: "string",
            },
            {
              name: "people[0].title",
              customName: "decision_maker_title",
              type: "string",
            },
            {
              name: "people[0].email",
              customName: "decision_maker_email",
              type: "string",
            },
            {
              name: "people[0].linkedin_url",
              customName: "decision_maker_linkedin",
              type: "string",
            },
            {
              name: "pagination.total_entries",
              customName: "total_contacts_found",
              type: "number",
            },
          ],
        },
      }),

      // 6. Discover contact information via Hunter
      n("6", "hitApi", "Verify Decision Maker Contact via Hunter", 1240, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hunter.io/v2/email-verifier?email={decision_maker_email}&api_key={hunter_api_key}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "data.status",
              customName: "email_verification_status",
              type: "string",
            },
            {
              name: "data.score",
              customName: "email_verification_score",
              type: "number",
            },
            {
              name: "data.result",
              customName: "verification_result",
              type: "string",
            },
          ],
        },
      }),

      // 7. Branch by partnership type
      n("7", "condition", "Route by Partnership Tier", 1480, {
        conditionDetails: {
          conditions: [
            {
              variable: "partnership_tier",
              operator: "equals",
              value: "strategic",
            },
          ],
          logicalOperator: "AND",
        },
      }),

      // 8. Send partnership pitch email
      emailNode(
        "8",
        "Send Partnership Pitch Email",
        1720,
        "{decision_maker_email}",
        "{pitch_subject}",
        "{pitch_body}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("7", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 1906. SUBJECT-LINE VARIANT GENERATOR (7 nodes)
  // ===================================================================

  "Subject-Line Variant Generator": {
    summary:
      "Fires when a subject line request arrives via webhook, AI analyzes the email content context, AI generates subject line variants, AI scores open-rate potential, ranks and selects top variants with runCode, branches by campaign type, and returns ranked variants to Slack.",
    nodes: [
      trigger("Webhook: Subject line variant request", "webhook_app", {
        source: "slack",
        event: "slash_command",
        howItStarts:
          "Fires when a marketer requests subject line variants via webhook, providing the email content, campaign type, audience segment, and desired tone for AI-powered subject line generation and scoring",
        setup:
          "Configure Slack slash command webhook for subject line requests. Set OpenAI API key for content analysis, variant generation, and scoring. Configure Slack webhook for variant delivery.",
        inputFields: [],
      }),

      // 2. AI extract subject line request context
      aiNode(
        "2",
        "AI Analyze Email Content Context",
        280,
        "You are an email marketing strategist. Parse the subject line request and extract: the email body/content summary, campaign type (promotional/newsletter/transactional/nurture/event), target audience segment, desired tone (professional/casual/urgent/playful), key message, any brand constraints, and character limit preferences. Respond JSON: {email_summary, campaign_type, audience_segment, desired_tone, key_message, brand_constraints, char_limit, sender_name, send_date}",
        "Raw webhook payload: {webhook_payload}. Extract all subject line request parameters regardless of field naming conventions.",
        [
          { field: "email_summary", as: "email_summary", type: "string" },
          { field: "campaign_type", as: "campaign_type", type: "string" },
          { field: "audience_segment", as: "audience_segment", type: "string" },
          { field: "desired_tone", as: "desired_tone", type: "string" },
          { field: "key_message", as: "key_message", type: "string" },
          {
            field: "brand_constraints",
            as: "brand_constraints",
            type: "string",
          },
          { field: "char_limit", as: "char_limit", type: "string" },
          { field: "sender_name", as: "sender_name", type: "string" },
        ],
      ),

      // 3. AI generate subject line variants
      aiNode(
        "3",
        "AI Generate Subject Line Variants",
        520,
        "You are a world-class email subject line copywriter. Generate 10 diverse subject line variants using different psychological triggers: curiosity, urgency, social proof, personalization, benefit-led, question-based, number-led, humor/wit, fear-of-missing-out, and direct/clear. Each should be under the character limit and match the desired tone. Respond JSON: {variant_1, variant_2, variant_3, variant_4, variant_5, variant_6, variant_7, variant_8, variant_9, variant_10, trigger_types}",
        "Email Content: {email_summary} | Campaign: {campaign_type} | Audience: {audience_segment} | Tone: {desired_tone} | Key Message: {key_message} | Brand: {brand_constraints} | Char Limit: {char_limit} | Sender: {sender_name}. Generate 10 psychologically diverse subject line variants.",
        [
          { field: "variant_1", as: "variant_1", type: "string" },
          { field: "variant_2", as: "variant_2", type: "string" },
          { field: "variant_3", as: "variant_3", type: "string" },
          { field: "variant_4", as: "variant_4", type: "string" },
          { field: "variant_5", as: "variant_5", type: "string" },
          { field: "variant_6", as: "variant_6", type: "string" },
          { field: "variant_7", as: "variant_7", type: "string" },
          { field: "variant_8", as: "variant_8", type: "string" },
          { field: "variant_9", as: "variant_9", type: "string" },
          { field: "variant_10", as: "variant_10", type: "string" },
          { field: "trigger_types", as: "trigger_types", type: "string" },
        ],
      ),

      // 4. AI score open-rate potential
      aiNode(
        "4",
        "AI Score Open-Rate Potential",
        760,
        "You are an email deliverability and engagement analyst. Score each of the 10 subject line variants on predicted open rate (1-100), considering: clarity, emotional trigger strength, spam risk, mobile preview optimization, and audience match. Rank them from best to worst. Respond JSON: {scores, top_3_ranked, spam_risk_flags, mobile_preview_notes, recommendation}",
        "Variants: 1) {variant_1} | 2) {variant_2} | 3) {variant_3} | 4) {variant_4} | 5) {variant_5} | 6) {variant_6} | 7) {variant_7} | 8) {variant_8} | 9) {variant_9} | 10) {variant_10} | Campaign: {campaign_type} | Audience: {audience_segment} | Triggers: {trigger_types}. Score each variant 1-100.",
        [
          { field: "scores", as: "variant_scores", type: "string" },
          { field: "top_3_ranked", as: "top_3_subjects", type: "string" },
          { field: "spam_risk_flags", as: "spam_risks", type: "string" },
          { field: "mobile_preview_notes", as: "mobile_notes", type: "string" },
          { field: "recommendation", as: "ai_recommendation", type: "string" },
        ],
      ),

      // 5. Rank and select top variants runCode
      n("5", "runCode", "Rank and Select Top Subject Line Variants", 1000, {
        runCodeDetails: {
          code: `const top3 = variables.top_3_subjects || '';
const scores = variables.variant_scores || '';
const spamRisks = variables.spam_risks || '';
const campaignType = variables.campaign_type || '';
const charLimit = parseInt(variables.char_limit || '60');
const variants = [
  variables.variant_1, variables.variant_2, variables.variant_3,
  variables.variant_4, variables.variant_5, variables.variant_6,
  variables.variant_7, variables.variant_8, variables.variant_9,
  variables.variant_10,
].filter(Boolean);
const withinLimit = variants.filter(v => v.length <= charLimit);
const overLimit = variants.length - withinLimit.length;
output.total_variants_generated = variants.length;
output.variants_within_limit = withinLimit.length;
output.variants_over_limit = overLimit;
output.top_3_formatted = top3;
output.best_subject = variants[0] || '';
output.selection_summary = 'Generated ' + variants.length + ' variants | ' + withinLimit.length + ' within ' + charLimit + ' chars | Top 3: ' + top3;`,
          outputVariables: [
            { name: "total_variants_generated", type: "number" },
            { name: "variants_within_limit", type: "number" },
            { name: "variants_over_limit", type: "number" },
            { name: "top_3_formatted", type: "string" },
            { name: "best_subject", type: "string" },
            { name: "selection_summary", type: "string" },
          ],
        },
      }),

      // 6. Branch by campaign type
      n("6", "condition", "Route by Campaign Type", 1240, {
        conditionDetails: {
          conditions: [
            {
              variable: "campaign_type",
              operator: "equals",
              value: "promotional",
            },
          ],
          logicalOperator: "AND",
        },
      }),

      // 7. Return ranked variants Slack
      slackNode(
        "7",
        "Deliver Ranked Subject Line Variants to Slack",
        1480,
        "Subject Line Variants Generated\\n\\nCampaign: {campaign_type} | Audience: {audience_segment} | Tone: {desired_tone}\\n\\nTOP 3 RECOMMENDED:\\n{top_3_formatted}\\n\\nALL VARIANTS:\\n1. {variant_1}\\n2. {variant_2}\\n3. {variant_3}\\n4. {variant_4}\\n5. {variant_5}\\n6. {variant_6}\\n7. {variant_7}\\n8. {variant_8}\\n9. {variant_9}\\n10. {variant_10}\\n\\nScores: {variant_scores}\\nSpam Risks: {spam_risks}\\nMobile Notes: {mobile_notes}\\n\\nRecommendation: {ai_recommendation}\\n\\nStats: {selection_summary}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("6", "7", "output_2"),
    ],
  },

  // ===================================================================
  // 1907. SUPPORT FRUSTRATION SAVE PLAY (9 nodes)
  // ===================================================================

  "Support Frustration Save Play": {
    summary:
      "Fires when a support event arrives via webhook, AI analyzes sentiment, fetches customer account context from HubSpot, scores churn risk with runCode, branches by risk severity, AI drafts personalized save outreach, sends a save email, creates an executive escalation in HubSpot, and alerts the save team via Slack.",
    nodes: [
      trigger("Webhook: Negative support interaction detected", "webhook_app", {
        source: "intercom",
        event: "conversation.rated",
        howItStarts:
          "Fires when a support conversation receives a negative rating or frustration signals are detected, triggering an automated churn prevention play with sentiment analysis, personalized outreach, and executive escalation",
        setup:
          "Configure Intercom webhook for conversation rating events. Set HubSpot API token for customer context and escalation. Set OpenAI API key for sentiment analysis and save outreach. Configure email sending credentials. Configure Slack webhook for save team alerts.",
        inputFields: [],
      }),

      // 2. AI analyze sentiment
      aiNode(
        "2",
        "AI Analyze Support Interaction Sentiment",
        280,
        "You are a customer sentiment analysis expert. Analyze the raw support event data to extract: customer name, email, company, conversation topic, frustration indicators, sentiment score (-100 to 100), specific complaint details, number of back-and-forth messages, resolution status, and escalation history. Respond JSON: {customer_name, customer_email, customer_company, conversation_topic, frustration_indicators, sentiment_score, complaint_details, message_count, resolution_status, escalation_count, support_rating}",
        "Raw webhook payload: {webhook_payload}. Extract all support interaction sentiment data regardless of field naming conventions.",
        [
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_company", as: "customer_company", type: "string" },
          {
            field: "conversation_topic",
            as: "conversation_topic",
            type: "string",
          },
          {
            field: "frustration_indicators",
            as: "frustration_indicators",
            type: "string",
          },
          { field: "sentiment_score", as: "sentiment_score", type: "number" },
          {
            field: "complaint_details",
            as: "complaint_details",
            type: "string",
          },
          { field: "message_count", as: "message_count", type: "string" },
          {
            field: "resolution_status",
            as: "resolution_status",
            type: "string",
          },
          { field: "support_rating", as: "support_rating", type: "string" },
        ],
      ),

      // 3. Fetch customer account context HubSpot
      hubspotPostNode(
        "3",
        "Fetch Customer Account Context from HubSpot",
        520,
        "crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{customer_email}"}]}]',
          },
          {
            key: "properties",
            value:
              '["email","firstname","lastname","company","lifecyclestage","hs_lead_status","recent_deal_amount","num_associated_deals","hs_analytics_source","createdate","notes_last_updated"]',
          },
        ],
        [
          {
            name: "results[0].id",
            customName: "hubspot_contact_id",
            type: "string",
          },
          {
            name: "results[0].properties.recent_deal_amount",
            customName: "account_value",
            type: "string",
          },
          {
            name: "results[0].properties.num_associated_deals",
            customName: "deal_count",
            type: "string",
          },
          {
            name: "results[0].properties.lifecyclestage",
            customName: "lifecycle_stage",
            type: "string",
          },
          {
            name: "results[0].properties.createdate",
            customName: "customer_since",
            type: "string",
          },
          {
            name: "results[0].properties.notes_last_updated",
            customName: "account_notes",
            type: "string",
          },
        ],
      ),

      // 4. Score churn risk runCode
      n("4", "runCode", "Score Customer Churn Risk Level", 760, {
        runCodeDetails: {
          code: `const sentimentScore = parseInt(variables.sentiment_score || '0');
const accountValue = parseFloat(variables.account_value || '0');
const dealCount = parseInt(variables.deal_count || '0');
const messageCount = parseInt(variables.message_count || '0');
const resolution = (variables.resolution_status || '').toLowerCase();
const rating = (variables.support_rating || '').toLowerCase();
const customerSince = new Date(variables.customer_since || 0);
const tenureMonths = Math.floor((Date.now() - customerSince.getTime()) / (30 * 86400000));
let churnRisk = 0;
if (sentimentScore < -50) churnRisk += 35;
else if (sentimentScore < -20) churnRisk += 20;
else if (sentimentScore < 0) churnRisk += 10;
if (rating === 'bad' || rating === '1' || rating === '2') churnRisk += 20;
if (messageCount > 10) churnRisk += 15;
else if (messageCount > 5) churnRisk += 8;
if (resolution === 'unresolved' || resolution === 'open') churnRisk += 20;
if (tenureMonths < 3) churnRisk += 10;
else if (tenureMonths > 24) churnRisk -= 5;
const riskSeverity = churnRisk >= 70 ? 'critical' : churnRisk >= 45 ? 'high' : churnRisk >= 25 ? 'medium' : 'low';
const isHighValueAccount = accountValue >= 10000;
const needsExecEscalation = (riskSeverity === 'critical') || (riskSeverity === 'high' && isHighValueAccount);
output.churn_risk_score = Math.min(100, churnRisk);
output.risk_severity = riskSeverity;
output.is_high_value = isHighValueAccount;
output.needs_exec_escalation = needsExecEscalation;
output.tenure_months = tenureMonths;
output.risk_summary = customer_name + ' at ' + variables.customer_company + ': Churn risk ' + churnRisk + '/100 (' + riskSeverity + ') | $' + accountValue + ' account | ' + tenureMonths + 'mo tenure | Sentiment: ' + sentimentScore + ' | Escalation: ' + needsExecEscalation;`,
          outputVariables: [
            { name: "churn_risk_score", type: "number" },
            { name: "risk_severity", type: "string" },
            { name: "is_high_value", type: "boolean" },
            { name: "needs_exec_escalation", type: "boolean" },
            { name: "tenure_months", type: "number" },
            { name: "risk_summary", type: "string" },
          ],
        },
      }),

      // 5. Branch by risk severity
      n("5", "condition", "Route by Churn Risk Severity", 1000, {
        conditionDetails: {
          conditions: [
            {
              variable: "risk_severity",
              operator: "equals",
              value: "critical",
            },
          ],
          logicalOperator: "AND",
        },
      }),

      // 6. AI personalized save outreach
      aiNode(
        "6",
        "AI Draft Personalized Save Outreach",
        1240,
        "You are a customer retention expert. Draft a sincere, empathetic save email that acknowledges the frustration, takes responsibility, offers a concrete resolution or gesture (account credit, executive call, priority support), and reinforces the relationship value. Avoid corporate-speak — be genuinely human. Under 150 words. Respond JSON: {subject_line, email_body, save_offer, escalation_context, follow_up_timing}",
        "Customer: {customer_name} at {customer_company} | Complaint: {complaint_details} | Topic: {conversation_topic} | Frustration: {frustration_indicators} | Sentiment: {sentiment_score} | Risk: {risk_severity} ({churn_risk_score}/100) | Account Value: ${account_value} | Tenure: {tenure_months} months | Resolution: {resolution_status} | Rating: {support_rating} | Needs Exec Escalation: {needs_exec_escalation}",
        [
          { field: "subject_line", as: "save_subject", type: "string" },
          { field: "email_body", as: "save_body", type: "string" },
          { field: "save_offer", as: "save_offer", type: "string" },
          {
            field: "escalation_context",
            as: "escalation_context",
            type: "string",
          },
          { field: "follow_up_timing", as: "follow_up_timing", type: "string" },
        ],
      ),

      // 7. Send save email
      emailNode(
        "7",
        "Send Customer Save Email",
        1480,
        "{customer_email}",
        "{save_subject}",
        "{save_body}",
      ),

      // 8. Create executive escalation HubSpot
      hubspotPostNode(
        "8",
        "Create Executive Escalation in HubSpot",
        1720,
        "crm/v3/objects/tasks",
        [
          {
            key: "properties",
            value:
              '{"hs_task_subject":"SAVE PLAY: {customer_name} at {customer_company} — {risk_severity} churn risk","hs_task_body":"Churn Risk: {churn_risk_score}/100 ({risk_severity})\\nAccount Value: ${account_value} | Tenure: {tenure_months} months\\nComplaint: {complaint_details}\\nSentiment: {sentiment_score}\\nSave Offer: {save_offer}\\nEscalation: {escalation_context}\\nFollow-Up: {follow_up_timing}\\nResolution Status: {resolution_status}","hs_task_status":"NOT_STARTED","hs_task_priority":"HIGH","hs_timestamp":"' +
              new Date().toISOString() +
              '"}',
          },
        ],
        [
          { name: "id", customName: "escalation_task_id", type: "string" },
          {
            name: "properties.hs_task_subject",
            customName: "confirmed_escalation",
            type: "string",
          },
        ],
      ),

      // 9. Alert save team Slack
      slackNode(
        "9",
        "Alert Save Team via Slack",
        1960,
        "CUSTOMER SAVE PLAY TRIGGERED\\n\\nCustomer: {customer_name} at {customer_company}\\nChurn Risk: {churn_risk_score}/100 ({risk_severity})\\nAccount Value: ${account_value} | Tenure: {tenure_months} months\\n\\nComplaint: {complaint_details}\\nSentiment Score: {sentiment_score} | Rating: {support_rating}\\nResolution: {resolution_status} | Messages: {message_count}\\n\\nSave Offer: {save_offer}\\nEscalation: {escalation_context}\\nFollow-Up Timing: {follow_up_timing}\\n\\nSave email sent to {customer_email}\\nHubSpot escalation task created: {escalation_task_id}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("5", "6", "output_2"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },
};
